import "./excavationPermitButtons.css";
// ExcavationPermitViewPage.jsx

import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Chip,
  Stack,
  Paper,
  Button,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  IconButton,
} from "@mui/material";
import "react-datepicker/dist/react-datepicker.css";
import API_URL from "../../src/Config";
import { LuCircleCheck, LuCircleX, LuSave, LuUpload } from "react-icons/lu";
import { ArrowLeft, Info, Settings, FileText, Building2, UserRound, MapPin, Layers, Users, CalendarDays, Clock3, Ruler, MoveHorizontal, MoveVertical, HardHat, ChevronRight } from "lucide-react";
import "./excavationDepartmentReview.css";
import DepartmentReassignAction from "./DepartmentReassignAction";
import { getExcavationWorkLocation } from "./excavationViewData";

export default function ExcavationPermitViewPage({
  isSidebarExpanded,
  fetchComplitionSatusData,
}) {
  const navigate = useNavigate();
  const { permit_id, department_name, wp_status } = useParams();
  const todayInputDate = new Date().toISOString().slice(0, 10);
  const reviewerName = localStorage.getItem("userName") || "";

  const [isLoading, setIsLoading] = useState(false);
  const [reassignOpen, setReassignOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [severity, setSeverity] = useState("success");

  const [excavationWorkPermit, setExcavationWorkPermit] = useState("");
  const [form1Data, setForm1Data] = useState({
    workPermitName: "",
    formData: {},
    dateValues: {},
    equipmentDetails: [],
  });

  // Pending permits (for departments that can review)
  const [pendingPermits, setPendingPermits] = useState([]);

  // Department observations fetched from my-observations API
  const [departmentObservations, setDepartmentObservations] = useState([]);

  // Local editable state for current department form
  const [departmentFormData, setDepartmentFormData] = useState({
    observations: "",
    Date: todayInputDate,
    "Name & Designation": reviewerName,
  });
  const [hasSavedDepartmentData, setHasSavedDepartmentData] = useState(false);
  const [isDepartmentDataDirty, setIsDepartmentDataDirty] = useState(false);

  // department meta (from my-observations response): approval_id, department_id, can_approve, can_reject
  const [departmentInfo, setDepartmentInfo] = useState(null);

  // Approval popup states
  const [showApprovePopup, setShowApprovePopup] = useState(false);
  const [showRejectPopup, setShowRejectPopup] = useState(false);
  const [excavationRemark, setExcavationRemark] = useState("");
  const [excavationFile, setExcavationFile] = useState(null);

  const normalizeDepartmentName = (value = "") =>
    value
      .toString()
      .toLowerCase()
      .replace(/&/g, "and")
      .replace(/\bdepartment\b/g, "")
      .replace(/\s+/g, " ")
      .trim();

  const getDepartmentFieldDefinitions = (currentDepartmentName = "") => {
    const normalizedDepartmentName = normalizeDepartmentName(currentDepartmentName);

    if (normalizedDepartmentName.includes("mechanical")) {
      return [
        {
          field_name:
            "Description of underground facilities existing at the proposed location",
          field_type: "text",
        },
        {
          field_name: "Precautions to be observed for safe excavation job",
          field_type: "text",
        },
        { field_name: "Date", field_type: "text" },
        { field_name: "Name & Designation", field_type: "text" },
        { field_name: "Signature", field_type: "text" },
      ];
    }

    if (
      normalizedDepartmentName.includes("electri") ||
      normalizedDepartmentName.includes("electician")
    ) {
      return [
        {
          field_name:
            "Details of underground cables present in the area to be excavated",
          field_type: "text",
        },
        { field_name: "Electrical isolation required", field_type: "text" },
        { field_name: "Date", field_type: "text" },
        { field_name: "Name & Designation", field_type: "text" },
        { field_name: "Signature", field_type: "text" },
      ];
    }

    if (normalizedDepartmentName.includes("instrument")) {
      return [
        {
          field_name:
            "Details of underground cables present in the area to be excavated",
          field_type: "text",
        },
        {
          field_name: "Precautions to be observed for safe excavation job",
          field_type: "text",
        },
        { field_name: "Date", field_type: "text" },
        { field_name: "Name & Designation", field_type: "text" },
        { field_name: "Signature", field_type: "text" },
      ];
    }

    if (
      normalizedDepartmentName.includes("fire") ||
      normalizedDepartmentName.includes("safety")
    ) {
      return [
        { field_name: "Cross over", field_type: "boolean" },
        { field_name: "Barricades", field_type: "boolean" },
        { field_name: "Road blocks", field_type: "boolean" },
        { field_name: "Shoring", field_type: "boolean" },
        { field_name: "Others", field_type: "boolean" },
        { field_name: "excavation_type", field_type: "text" },
        { field_name: "Gas Masks", field_type: "boolean" },
        { field_name: "Eye Protection", field_type: "boolean" },
        { field_name: "Ear Protection", field_type: "boolean" },
        { field_name: "Wooden Handheld tools", field_type: "boolean" },
        { field_name: "Safety Others", field_type: "boolean" },
        {
          field_name: "Emergency approach for vehicles",
          field_type: "boolean",
        },
        { field_name: "Barricading", field_type: "boolean" },
        { field_name: "Road closure", field_type: "boolean" },
        { field_name: "Other precautions", field_type: "text" },
      ];
    }

    return [];
  };

  const buildDepartmentObservationInputs = (
    currentDepartmentName,
    currentFormData
  ) => {
    const configuredFields = getDepartmentFieldDefinitions(currentDepartmentName);

    if (configuredFields.length > 0) {
      return configuredFields.reduce((accumulator, field) => {
        const rawValue = currentFormData[field.field_name];

        if (field.field_type === "boolean") {
          accumulator.push({
            field_name: field.field_name,
            field_value: rawValue === true ? "true" : "false",
            field_type: "boolean",
          });
          return accumulator;
        }

        if (
          rawValue !== null &&
          rawValue !== undefined &&
          String(rawValue).trim() !== ""
        ) {
          accumulator.push({
            field_name: field.field_name,
            field_value: String(rawValue).trim(),
            field_type: field.field_type || "text",
          });
        }

        return accumulator;
      }, []);
    }

    return Object.entries(currentFormData).reduce((accumulator, [key, value]) => {
      if (key === "observations") {
        return accumulator;
      }

      if (typeof value === "boolean") {
        accumulator.push({
          field_name: key,
          field_value: value ? "true" : "false",
          field_type: "boolean",
        });
        return accumulator;
      }

      if (value !== null && value !== undefined && String(value).trim() !== "") {
        accumulator.push({
          field_name: key,
          field_value: String(value).trim(),
          field_type: typeof value === "number" ? "number" : "text",
        });
      }

      return accumulator;
    }, []);
  };

  // Utility - snackbar
  const showSnackbar = (message, type = "success") => {
    setSnackbarMessage(message);
    setSeverity(type);
    setSnackbarOpen(true);
  };
  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  const handleCancelClick = () => {
    navigate("/excavation-department-review");
  };

  // -------- API calls sequence --------
  // 1) fetch permit details
  const fetchPermitDetails = async () => {
    try {
      const authKey = localStorage.getItem("authKey");
      const response = await axios.get(
        `${API_URL}/api/get_permit_work_details/${permit_id}`,
        { headers: { Authorization: authKey } }
      );

      const permit = response.data?.permit_details?.[0];
      if (!permit) {
        showSnackbar("No permit details found", "warning");
        return;
      }

      setExcavationWorkPermit(permit?.work_permit_name || "");

      const formatDate = (dateString) => {
        if (!dateString) return null;
        const d = new Date(dateString);
        d.setMinutes(d.getMinutes() + d.getTimezoneOffset());
        return d;
      };

      const dependentFieldValues = {};
      const dependentFieldsFromPermit = permit.issued_to?.dependent_fields || [];
      dependentFieldsFromPermit.forEach((dep) => {
        dependentFieldValues[dep.field_label] = dep.value;
      });

      setForm1Data({
        workPermitName: permit?.work_permit_name || "",
        formData: {
          permit_type_id: permit?.permit_type_id || "",
          issued_to: permit?.issued_to?.value || "",
          ...dependentFieldValues,
          department: permit?.department_name || "",
          receiver_name: permit?.receiver_name || "",
          contractor: permit?.contractor || "",
          section: permit?.section || "",
          area: permit?.area || "",
          unit: permit?.unit || "",
          work_description: permit?.work_description || "",
          work_type: permit?.work_type || "",
          related_permit_no: permit?.related_permit_no || "",
          no_of_men: permit?.no_of_men || "",
          work_location: permit?.work_location || "",
          facility_location: permit?.facility_location || "",
          excavation_length: permit?.excavation_length || "",
          excavation_width: permit?.excavation_width || "",
          excavation_depth: permit?.excavation_depth || "",
          created_at: permit?.created_at || "",
          updated_at: permit?.updated_at || "",
        },
        dateValues: {
          "Start Date & Time": permit.valid_from
            ? formatDate(permit.valid_from)
            : null,
          "End Date & Time": permit.valid_to
            ? formatDate(permit.valid_to)
            : null,
          "Work Time From": permit.work_time_from
            ? formatDate(permit.work_time_from)
            : null,
          "Work Time To": permit.work_time_to
            ? formatDate(permit.work_time_to)
            : null,
        },
        equipmentDetails: permit?.equipment_details || [],
      });
    } catch (error) {
      console.error("Failed to load permit details:", error);
      showSnackbar("Failed to load permit details", "error");
    }
  };

  // 2) fetch department/pending_permits to get list of review departments for the current user
  const fetchPendingPermits = async () => {
    try {
      const authKey = localStorage.getItem("authKey");
      const response = await axios.get(`${API_URL}/api/department/pending_permits`, {
        headers: { Authorization: authKey },
      });
      const permits = response.data?.permits || [];
      setPendingPermits(permits);
      return permits;
    } catch (error) {
      console.error("Failed to load pending permits:", error);
      showSnackbar("Failed to load pending permits", "error");
      return [];
    }
  };

  // 3) fetch my department observations and prefill
  const fetchDepartmentObservations = async () => {
    try {
      const authKey = localStorage.getItem("authKey");
      const response = await axios.get(
        `${API_URL}/api/permit/department/my-observations/${permit_id}`,
        { headers: { Authorization: authKey } }
      );

      const approvals = response.data?.approvals || [];
      setDepartmentObservations(approvals);

      // find matching approval for department_name param (case-insensitive)
      const matchingObs = approvals.find(
        (a) =>
          normalizeDepartmentName(a.department_name) ===
          normalizeDepartmentName(department_name)
      );

      if (matchingObs) {
        const formData = {
          observations: matchingObs.observations || "",
          Date: todayInputDate,
          "Name & Designation": reviewerName,
        };
        const hasSavedInputs = (matchingObs.inputs || []).some((inp) => {
          if (inp.field_type === "boolean") {
            return (
              inp.field_value === "true" ||
              inp.field_value === true ||
              inp.field_value === "false" ||
              inp.field_value === false
            );
          }

          return (
            inp.field_value !== null &&
            inp.field_value !== undefined &&
            String(inp.field_value).trim() !== ""
          );
        });
        const hasSavedObservations =
          matchingObs.observations &&
          String(matchingObs.observations).trim() !== "";

        (matchingObs.inputs || []).forEach((inp) => {
          const value =
            inp.field_type === "boolean"
              ? inp.field_value === "true" || inp.field_value === true
              : inp.field_value;
          formData[inp.field_name] = value;
        });
        formData.Date = formData.Date || todayInputDate;
        formData["Name & Designation"] =
          formData["Name & Designation"] || reviewerName;

        setDepartmentFormData(formData);
        setHasSavedDepartmentData(Boolean(hasSavedInputs || hasSavedObservations));
        setIsDepartmentDataDirty(false);
        setDepartmentInfo({
          approval_id: matchingObs.approval_id,
          department_id: matchingObs.department_id,
          department_name: matchingObs.department_name,
          approval_status: matchingObs.approval_status,
          can_approve: matchingObs.can_approve,
          can_reject: matchingObs.can_reject,
        });
      } else {
        // No existing approval record — departmentInfo still could be set from pendingPermits lookup
        setDepartmentFormData((prev) => ({
          ...prev,
          observations: "",
          Date: prev.Date || todayInputDate,
          "Name & Designation": prev["Name & Designation"] || reviewerName,
        }));
        setHasSavedDepartmentData(false);
        setIsDepartmentDataDirty(false);
      }
      return approvals;
    } catch (error) {
      // 403 may indicate user not assigned to any department — handle silently or show message
      console.error("Failed to load department observations:", error);
      if (error.response?.status !== 403) {
        showSnackbar("Failed to load department observations", "error");
      }
      return [];
    }
  };

  // Combined initial load: first permit details, then pending permits (to determine if current department is reviewer), then my-observations.
  useEffect(() => {
    if (!permit_id || !department_name) return;

    (async () => {
      await fetchPermitDetails();
      const permits = await fetchPendingPermits();
      const approvals = await fetchDepartmentObservations();

      const normalizedDepartmentName = normalizeDepartmentName(department_name);

      const matchingObservation = approvals.find(
        (approval) =>
          normalizeDepartmentName(approval.department_name) ===
          normalizedDepartmentName
      );

      if (matchingObservation) {
        setDepartmentInfo((prev) => ({
          ...(prev || {}),
          approval_id: matchingObservation.approval_id,
          department_id: matchingObservation.department_id,
          department_name: matchingObservation.department_name,
          approval_status: matchingObservation.approval_status,
          can_approve: matchingObservation.can_approve,
          can_reject: matchingObservation.can_reject,
        }));
        return;
      }

      // Attempt to find department metadata from pending permits when observations are still empty.
      try {
        const matchingPending = permits.find(
          (p) =>
            String(p.permit_id) === String(permit_id) &&
            normalizeDepartmentName(
              p.review_department || p.review_department_name || p.department
            ) === normalizedDepartmentName
        );
        // Note: if your pending_permits returns review_department or review_department_id differently adapt this.
        if (matchingPending) {
          // we can set departmentInfo if not already set by observations
          setDepartmentInfo((prev) => ({
            approval_id: prev?.approval_id || matchingPending.approval_id,
            department_id:
              prev?.department_id ||
              matchingPending.review_department_id ||
              matchingPending.department_id,
            department_name:
              prev?.department_name ||
              matchingPending.review_department ||
              matchingPending.review_department_name ||
              matchingPending.department,
            approval_status:
              prev?.approval_status || matchingPending.approval_status,
            can_approve: prev?.can_approve ?? matchingPending.can_approve ?? true,
            can_reject: prev?.can_reject ?? matchingPending.can_reject ?? true,
          }));
        }
      } catch (e) {
        // ignore
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permit_id, department_name]);

  // -------- handlers for form fields --------
  const handleFieldChange = (fieldName, value) => {
    setDepartmentFormData((prev) => ({ ...prev, [fieldName]: value }));
    setIsDepartmentDataDirty(true);
  };

  // Save department observations (POST to /api/permit/department/observations)
  const handleSaveDepartment = async () => {
    if (!departmentInfo || !departmentInfo.department_id) {
      showSnackbar("Department information not found or you are not reviewer for this department", "error");
      return;
    }

    setIsLoading(true);
    try {
      const authKey = localStorage.getItem("authKey");
      const inputs = buildDepartmentObservationInputs(
        department_name,
        departmentFormData
      );

      const payload = {
        permit_id: permit_id,
        department_id: departmentInfo.department_id,
        observations: departmentFormData.observations || "",
        inputs: inputs,
      };

      const response = await axios.post(
        `${API_URL}/api/permit/department/observations`,
        payload,
        {
          headers: {
            Authorization: authKey,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 200) {
        showSnackbar("Department observations saved successfully!", "success");
        setHasSavedDepartmentData(true);
        setIsDepartmentDataDirty(false);
        // reload department observations & completion status
        await fetchDepartmentObservations();
        fetchComplitionSatusData && fetchComplitionSatusData();
      } else {
        showSnackbar("Unexpected response from server while saving.", "warning");
      }
    } catch (error) {
      console.error("Error saving department observations:", error);
      showSnackbar(
        error.response?.data?.message || "Error saving department observations.",
        "error"
      );
    } finally {
      setIsLoading(false);
    }
  };

  // -------- Approve / Reject flow --------
  const isDepartmentApproved =
    departmentInfo?.approval_status === "Approved";
  const isDepartmentSaveRequired =
    !hasSavedDepartmentData || isDepartmentDataDirty;
  const isSaveDisabled =
    isLoading || reassignOpen || !departmentInfo || isDepartmentApproved;
  const isRejectDisabled =
    !departmentInfo?.can_reject ||
    reassignOpen ||
    isLoading ||
    isDepartmentApproved;
  const isApproveDisabled =
    !departmentInfo?.can_approve ||
    reassignOpen ||
    isLoading ||
    isDepartmentApproved ||
    isDepartmentSaveRequired;
  const approveActionTooltip = isDepartmentSaveRequired
    ? "Save department details first."
    : isDepartmentApproved
    ? "This department is already approved."
    : "";
  const rejectActionTooltip = isDepartmentApproved
    ? "This department is already approved."
    : !departmentInfo?.can_reject
    ? "You do not have permission to reject this permit."
    : "";

  const openApprovePopup = () => {
    if (isDepartmentApproved) {
      showSnackbar("This department is already approved.", "info");
      return;
    }
    if (isDepartmentSaveRequired) {
      showSnackbar("Save department details first.", "info");
      return;
    }
    setExcavationRemark("");
    setExcavationFile(null);
    setShowApprovePopup(true);
  };
  const closeApprovePopup = () => setShowApprovePopup(false);

  const openRejectPopup = () => {
    if (isDepartmentApproved) {
      showSnackbar("This department is already approved.", "info");
      return;
    }
    setExcavationRemark("");
    setExcavationFile(null);
    setShowRejectPopup(true);
  };
  const closeRejectPopup = () => setShowRejectPopup(false);

  const handleExcavationFileChange = (e) => {
    const file = e.target.files?.[0] || null;
    setExcavationFile(file);
  };

  const doApproveOrReject = async (action) => {
    if (!departmentInfo?.department_id) {
      showSnackbar("Department info missing. Can't perform action.", "error");
      return;
    }

    setIsLoading(true);
    try {
      const authKey = localStorage.getItem("authKey");
      const fd = new FormData();

      fd.append("permit_id", permit_id);
      fd.append("department_id", departmentInfo.department_id);
      fd.append("action", action); // Approve or Reject
      fd.append("remarks", excavationRemark || "");

      if (excavationFile) {
        fd.append("attachments", excavationFile);
      }

      const response = await axios.post(`${API_URL}/api/permit/department/approve`, fd, {
        headers: {
          Authorization: authKey,
          "Content-Type": "multipart/form-data",
        },
      });

      showSnackbar(response.data?.message || `${action} successful`, "success");

      // close popup and navigate back to list after success
      setIsLoading(false);
      setTimeout(() => {
        navigate("/excavation-department-review");
      }, 300); // small delay to show snackbar (non-blocking)
    } catch (error) {
      console.error(`${action} error:`, error);
      showSnackbar(
        error.response?.data?.message || `Failed to ${action.toLowerCase()} permit.`,
        "error"
      );
      setIsLoading(false);
    }
  };

  // helper to format date/time
  const formatDateTime = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Check if current dept is part of pending review for this permit (from pendingPermits)
  const isDeptReviewerForPermit = () => {
    try {
      const matching = pendingPermits.find(
        (p) =>
          String(p.permit_id) === String(permit_id) &&
          (normalizeDepartmentName(
            p.review_department || p.review_department_name || p.department
          ) === normalizeDepartmentName(department_name) ||
            p.review_department === department_name ||
            p.review_department?.includes?.(department_name))
      );
      return !!matching;
    } catch (e) {
      return false;
    }
  };

  // Render Work Permit details (same as your renderForm1)
  const renderForm1 = () => {
    const permit = form1Data?.formData || {};
    const dateValues = form1Data?.dateValues || {};
    const generalDetails = [
      ["Permit Type", form1Data?.workPermitName],
      ["Issued To Department", permit?.issued_to],
      ["Receiver Name", permit?.receiver_name],
      ["Work Location (Area / Unit)", getExcavationWorkLocation(permit)],
      ["Section", permit?.section],
    ];
    const workDetails = [
      ["Work Description", permit?.work_description],
      ["No. of Men", permit?.no_of_men],
      ["Start Date & Time", formatDateTime(dateValues["Start Date & Time"])],
      ["End Date & Time", formatDateTime(dateValues["End Date & Time"])],
      ["Work Time From", formatDateTime(dateValues["Work Time From"])],
      ["Work Time To", formatDateTime(dateValues["Work Time To"])],
    ];
    const excavationDetails = [
      ["Length", permit?.excavation_length],
      ["Width", permit?.excavation_width],
      ["Depth", permit?.excavation_depth],
    ].filter(([, value]) => value !== null && value !== undefined && value !== "");

    return (
      <Card
        className="excavation-review-summary"
        sx={{
          border: "1px solid #e1e7f0",
          borderRadius: 2,
          boxShadow: "0 8px 22px rgba(15, 23, 42, 0.05)",
          overflow: "hidden",
        }}
      >
        <CardContent sx={{ p: 2 }}>
          <Box
            sx={{
              alignItems: { xs: "flex-start", md: "center" },
              borderBottom: "1px solid #e8edf5",
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: 1,
              justifyContent: "space-between",
              mb: 1.5,
              pb: 1.5,
            }}
          >
            <Box className="excavation-review-title">
              <IconButton aria-label="Back to department review" onClick={handleCancelClick} className="excavation-review-back">
                <ArrowLeft size={20} />
              </IconButton>
              <Box>
              <Typography
                variant="h6"
                sx={{
                  color: "#1e3a8a",
                  fontSize: "1rem",
                  fontWeight: 800,
                  lineHeight: 1.25,
                }}
              >
                Excavation Clearance / Road Closure Work Permit
              </Typography>
              <Typography sx={{ color: "#64748b", fontSize: 12.5, mt: 0.25 }}>
                Review permit details, work timing, and excavation dimensions.
              </Typography>
              </Box>
            </Box>

            <Stack direction="row" spacing={0.75} sx={{ maxWidth: "100%" }} flexWrap="wrap" useFlexGap>
              {permit?.work_type && (
                <Chip label={permit?.work_type} color="primary" variant="outlined" size="small" />
              )}
              {permit?.contractor && (
                <Chip label={`Contractor: ${permit?.contractor}`} color="secondary" size="small" />
              )}
              {permit?.department && (
                <Chip label={`Dept: ${permit?.department}`} color="default" size="small" />
              )}
            </Stack>
          </Box>

          <Box
            className="excavation-review-summary-grid"
            sx={{
              display: "grid",
              gap: 1.25,
              gridTemplateColumns: { xs: "1fr", lg: "1.1fr 1.4fr" },
            }}
          >
            <DetailSection title="General Information" icon={Info}>
              {generalDetails.map(([label, value]) => (
                <DetailItem key={label} label={label} value={value} />
              ))}
            </DetailSection>

            <DetailSection title="Work Details" icon={Settings}>
              {workDetails.map(([label, value]) => (
                <DetailItem
                  key={label}
                  label={label}
                  value={value}
                />
              ))}
            </DetailSection>

            {excavationDetails.length > 0 && (
              <Paper
                className="excavation-review-dimensions"
                variant="outlined"
                sx={{
                  bgcolor: "#f8fafc",
                  borderColor: "#e2e8f0",
                  borderRadius: 2,
                  gridColumn: "1 / -1",
                  p: 1.5,
                }}
              >
                <Typography
                  className="excavation-review-section-title"
                  variant="subtitle2"
                  sx={{ color: "#334155", fontSize: 13, fontWeight: 800, mb: 1 }}
                >
                  <HardHat size={19} /> Excavation Details
                </Typography>
                <Box
                  sx={{
                    display: "grid",
                    gap: 1,
                    gridTemplateColumns: {
                      xs: "1fr",
                      sm: "repeat(3, minmax(0, 1fr))",
                    },
                  }}
                >
                  {excavationDetails.map(([label, value]) => (
                    <DetailItem key={label} label={`${label} (m)`} value={value} />
                  ))}
                </Box>
              </Paper>
            )}
          </Box>
        </CardContent>
      </Card>
    );
  };

  const DetailSection = ({ title, children, icon: SectionIcon }) => (
    <Paper
      className="excavation-review-detail-section"
      variant="outlined"
      sx={{
        borderColor: "#e2e8f0",
        borderRadius: 2,
        p: 1.5,
      }}
    >
      <Typography
        className="excavation-review-section-title"
        variant="subtitle2"
        sx={{ color: "#334155", fontSize: 13, fontWeight: 800, mb: 1 }}
      >
        {SectionIcon && <SectionIcon size={18} />} {title}
      </Typography>
      <Box
        sx={{
          display: "grid",
          gap: 1,
          gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
        }}
      >
        {children}
      </Box>
    </Paper>
  );

  const DetailItem = ({ label, value, sx }) => (
    <Box
      className="excavation-review-detail-item"
      sx={{
        bgcolor: "#f8fafc",
        border: "1px solid #eef2f7",
        borderRadius: 1.5,
        minWidth: 0,
        px: 1.25,
        py: 0.85,
        ...sx,
      }}
    >
      <span className="excavation-review-field-icon" aria-hidden="true">
        {React.createElement(reviewDetailIcons[label] || FileText, { size: 19, strokeWidth: 1.8 })}
      </span>
      <Box className="excavation-review-field-content">
      <Typography
        variant="caption"
        sx={{
          color: "#64748b",
          display: "block",
          fontSize: "0.7rem",
          fontWeight: 700,
          lineHeight: 1.2,
          mb: 0.25,
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: "#0f172a",
          fontSize: "0.82rem",
          fontWeight: 700,
          lineHeight: 1.35,
          overflowWrap: "anywhere",
        }}
      >
        {value === null || value === undefined || value === "" ? "—" : value}
      </Typography>
      </Box>
    </Box>
  );

  // Render department-specific fields inline (no small helper consts)
  const renderDepartmentForm = () => {
    if (!department_name) {
      return (
        <Paper sx={{ p: 3, textAlign: "center" }}>
          <Typography color="textSecondary">No department specified</Typography>
        </Paper>
      );
    }

    // If pendingPermits says this department isn't a reviewer for this permit, show a message
    const deptIsReviewer = isDeptReviewerForPermit();

    return (
      <Card className="excavation-review-form-card" sx={{ borderRadius: 2, boxShadow: 2 }}>
        <CardContent sx={{ p: 3 }}>
          <Typography className="excavation-review-form-heading" variant="h6" sx={{ mb: 2, fontWeight: 600, color: "primary.main" }}>
            <Settings size={20} /> {department_name} Department Review
          </Typography>
          <Typography className="excavation-review-form-description">
            Record your findings and precautions, then save the review before approving.
          </Typography>

          {/* {!deptIsReviewer && (
            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography color="textSecondary">
                You are not assigned as a reviewer for this department or this permit is not pending for this department. If you believe this is an error, contact the admin.
              </Typography>
            </Paper>
          )} */}

          {/* Inline rendering of fields based on department_name (order checks transform to lowercase) */}
          <Box className="excavation-review-fields">
            {(() => {
              const deptNameLower = (department_name || "").toLowerCase();

              if (deptNameLower.includes("mechanical")) {
                return (
                  <>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
                        Description of underground facilities existing at the proposed location
                      </Typography>
                      <textarea
                        rows={3}
                        value={departmentFormData["Description of underground facilities existing at the proposed location"] || ""} aria-label="Description of underground facilities existing at the proposed location"
                        onChange={(e) => handleFieldChange("Description of underground facilities existing at the proposed location", e.target.value)}
                        style={{ width: "100%", padding: 8, borderRadius: 4, border: "1px solid #ccc", fontSize: 14 }}
                      />
                    </Box>

                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
                        Precautions to be observed for safe excavation job
                      </Typography>
                      <textarea
                        rows={3}
                        value={departmentFormData["Precautions to be observed for safe excavation job"] || ""} aria-label="Precautions to be observed for safe excavation job"
                        onChange={(e) => handleFieldChange("Precautions to be observed for safe excavation job", e.target.value)}
                        style={{ width: "100%", padding: 8, borderRadius: 4, border: "1px solid #ccc", fontSize: 14 }}
                      />
                    </Box>

                    <Box className="excavation-review-signoff" sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>Date</Typography>
                        <input
                          type="date"
                          value={departmentFormData["Date"] || ""} aria-label="Date"
                          onChange={(e) => handleFieldChange("Date", e.target.value)}
                          style={{ width: "100%", padding: 8, borderRadius: 4, border: "1px solid #ccc" }}
                        />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>Name & Designation</Typography>
                        <input
                          value={departmentFormData["Name & Designation"] || ""} aria-label="Name &amp; Designation"
                          onChange={(e) => handleFieldChange("Name & Designation", e.target.value)}
                          style={{ width: "100%", padding: 8, borderRadius: 4, border: "1px solid #ccc" }}
                        />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>Signature</Typography>
                        <input
                          value={departmentFormData["Signature"] || ""} aria-label="Signature"
                          onChange={(e) => handleFieldChange("Signature", e.target.value)}
                          style={{ width: "100%", padding: 8, borderRadius: 4, border: "1px solid #ccc" }}
                        />
                      </Box>
                    </Box>
                  </>
                );
              }

              if (deptNameLower.includes("electri") || deptNameLower.includes("electician")) {
                return (
                  <>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
                        Details of underground cables present in the area to be excavated
                      </Typography>
                      <textarea
                        rows={3}
                        value={departmentFormData["Details of underground cables present in the area to be excavated"] || ""} aria-label="Details of underground cables present in the area to be excavated"
                        onChange={(e) => handleFieldChange("Details of underground cables present in the area to be excavated", e.target.value)}
                        style={{ width: "100%", padding: 8, borderRadius: 4, border: "1px solid #ccc", fontSize: 14 }}
                      />
                    </Box>

                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
                        Electrical isolation required
                      </Typography>
                      <textarea
                        rows={2}
                        value={departmentFormData["Electrical isolation required"] || ""} aria-label="Electrical isolation required"
                        onChange={(e) => handleFieldChange("Electrical isolation required", e.target.value)}
                        style={{ width: "100%", padding: 8, borderRadius: 4, border: "1px solid #ccc", fontSize: 14 }}
                      />
                    </Box>

                    <Box className="excavation-review-signoff" sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>Date</Typography>
                        <input
                          type="date"
                          value={departmentFormData["Date"] || ""} aria-label="Date"
                          onChange={(e) => handleFieldChange("Date", e.target.value)}
                          style={{ width: "100%", padding: 8, borderRadius: 4, border: "1px solid #ccc" }}
                        />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>Name & Designation</Typography>
                        <input
                          value={departmentFormData["Name & Designation"] || ""} aria-label="Name &amp; Designation"
                          onChange={(e) => handleFieldChange("Name & Designation", e.target.value)}
                          style={{ width: "100%", padding: 8, borderRadius: 4, border: "1px solid #ccc" }}
                        />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>Signature</Typography>
                        <input
                          value={departmentFormData["Signature"] || ""} aria-label="Signature"
                          onChange={(e) => handleFieldChange("Signature", e.target.value)}
                          style={{ width: "100%", padding: 8, borderRadius: 4, border: "1px solid #ccc" }}
                        />
                      </Box>
                    </Box>
                  </>
                );
              }

              if (deptNameLower.includes("instrument")) {
                return (
                  <>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
                        Details of underground cables present in the area to be excavated
                      </Typography>
                      <textarea
                        rows={3}
                        value={departmentFormData["Details of underground cables present in the area to be excavated"] || ""} aria-label="Details of underground cables present in the area to be excavated"
                        onChange={(e) => handleFieldChange("Details of underground cables present in the area to be excavated", e.target.value)}
                        style={{ width: "100%", padding: 8, borderRadius: 4, border: "1px solid #ccc", fontSize: 14 }}
                      />
                    </Box>

                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
                        Precautions to be observed for safe excavation job
                      </Typography>
                      <textarea
                        rows={3}
                        value={departmentFormData["Precautions to be observed for safe excavation job"] || ""} aria-label="Precautions to be observed for safe excavation job"
                        onChange={(e) => handleFieldChange("Precautions to be observed for safe excavation job", e.target.value)}
                        style={{ width: "100%", padding: 8, borderRadius: 4, border: "1px solid #ccc", fontSize: 14 }}
                      />
                    </Box>

                    <Box className="excavation-review-signoff" sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>Date</Typography>
                        <input
                          type="date"
                          value={departmentFormData["Date"] || ""} aria-label="Date"
                          onChange={(e) => handleFieldChange("Date", e.target.value)}
                          style={{ width: "100%", padding: 8, borderRadius: 4, border: "1px solid #ccc" }}
                        />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>Name & Designation</Typography>
                        <input
                          value={departmentFormData["Name & Designation"] || ""} aria-label="Name &amp; Designation"
                          onChange={(e) => handleFieldChange("Name & Designation", e.target.value)}
                          style={{ width: "100%", padding: 8, borderRadius: 4, border: "1px solid #ccc" }}
                        />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>Signature</Typography>
                        <input
                          value={departmentFormData["Signature"] || ""} aria-label="Signature"
                          onChange={(e) => handleFieldChange("Signature", e.target.value)}
                          style={{ width: "100%", padding: 8, borderRadius: 4, border: "1px solid #ccc" }}
                        />
                      </Box>
                    </Box>
                  </>
                );
              }

              if (deptNameLower.includes("fire") || deptNameLower.includes("safety")) {
                return (
                  <Box className="fire-safety-review-fields">
                    <SafetyChoiceGroup
                      title="Site protection"
                      description="Select the controls required to secure the work area."
                      icon={HardHat}
                      choices={["Cross over", "Barricades", "Road blocks", "Shoring", "Others"]}
                      values={departmentFormData}
                      onChange={handleFieldChange}
                    />
                    <SafetyChoiceGroup
                      title="Types of excavation"
                      description="Choose the excavation method for this work."
                      icon={Settings}
                      choices={["Manual", "Mechanical", "Hand tools", "Others"]}
                      type="radio"
                      field="excavation_type"
                      values={departmentFormData}
                      onChange={handleFieldChange}
                    />
                    <SafetyChoiceGroup
                      title="Safety equipment"
                      description="Select the protective equipment required at the site."
                      icon={UserRound}
                      choices={["Gas Masks", "Eye Protection", "Ear Protection", "Wooden Handheld tools", { field: "Safety Others", label: "Others" }]}
                      values={departmentFormData}
                      onChange={handleFieldChange}
                    />
                    <SafetyChoiceGroup
                      title="Emergency access & barricading"
                      description="Identify the access and traffic controls to provide."
                      icon={MapPin}
                      choices={["Emergency approach for vehicles", "Barricading", "Road closure"]}
                      values={departmentFormData}
                      onChange={handleFieldChange}
                    />
                    <Box className="fire-safety-precautions">
                      <label htmlFor="fire-safety-other-precautions">Other precautions</label>
                      <Typography className="fire-safety-group-description">
                        Add any additional precautions or site-specific instructions.
                      </Typography>
                      <textarea
                        id="fire-safety-other-precautions"
                        rows={3}
                        value={departmentFormData["Other precautions"] || ""}
                        onChange={(e) => handleFieldChange("Other precautions", e.target.value)}
                        placeholder="Describe any additional precautions…"
                        style={{ width: "100%", padding: 8, borderRadius: 4, border: "1px solid #ccc", fontSize: 14 }}
                      />
                    </Box>
                  </Box>
                );
              }

              // default fallback when no department-specific match found
              return (
                <Typography color="textSecondary">
                  No specific fields configured for this department: {department_name}
                </Typography>
              );
            })()}
          </Box>

          {/* Common observations textarea */}
          {/* <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Observations / Comments
            </Typography>
            <textarea
              rows={4}
              value={departmentFormData["observations"] || ""} aria-label="observations"
              onChange={(e) => handleFieldChange("observations", e.target.value)}
              style={{ width: "100%", padding: 8, borderRadius: 4, border: "1px solid #ccc", fontSize: 14 }}
            />
          </Box> */}

          <Paper
            className="excavation-review-action-panel"
            variant="outlined"
            sx={{
              alignItems: { xs: "stretch", md: "center" },
              bgcolor: "#f8fafc",
              borderColor: "#dfe7f3",
              borderRadius: 2,
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: 2,
              justifyContent: "space-between",
              mt: 3,
              p: 2,
            }}
          >
            <Box className="excavation-review-action-note" role="status" aria-live="polite">
              <Info size={19} aria-hidden="true" />
              <Box>
              <Typography sx={{ color: "#172033", fontSize: 14, fontWeight: 700 }}>
                Department review action
              </Typography>
              <Typography sx={{ color: "#64748b", fontSize: 12.5, mt: 0.4 }}>
                {isDepartmentApproved
                  ? "This department review is already approved."
                  : isDepartmentDataDirty
                  ? "You have unsaved review details. Save before approving, or reject directly with remarks."
                  : hasSavedDepartmentData
                  ? "Review details are saved. You can approve or reject this permit."
                  : "Save observations before approval. Rejection can be submitted directly."}
              </Typography>
              </Box>
            </Box>

            <Stack
              className="excavation-review-actions"
              direction={{ xs: "column", sm: "row" }}
              spacing={1.2}
              sx={{ flexShrink: 0 }}
            >
              <Button variant="outlined" onClick={handleCancelClick} startIcon={<ArrowLeft size={16} />} className="excavation-review-cancel excavation-permit-button">
                Cancel
              </Button>
              <DepartmentReassignAction
                permitId={permit_id}
                departmentId={departmentInfo?.department_id}
                departmentName={department_name}
                approvalStatus={departmentInfo?.approval_status}
                hasUnsavedChanges={isDepartmentDataDirty}
                disabled={isLoading || showApprovePopup || showRejectPopup}
                onBusyChange={setReassignOpen}
                onSuccess={message => {
                  fetchComplitionSatusData?.();
                  navigate("/excavation-department-review", { replace: true, state: { reassignmentMessage: message } });
                }}
              />
              <Button
                variant="contained"
                onClick={handleSaveDepartment}
                disabled={isSaveDisabled}
                startIcon={
                  isLoading ? <CircularProgress size={16} color="inherit" /> : <LuSave />
                }
                sx={reviewActionButtonSx.save}
              >
                {isLoading ? "Saving..." : "Save Review"}
              </Button>

              <Tooltip title={rejectActionTooltip} arrow>
                <span style={{ display: "inline-flex" }}>
                  <Button
                    variant="outlined"
                    onClick={openRejectPopup}
                    disabled={isRejectDisabled}
                    startIcon={<LuCircleX />}
                    className="excavation-permit-button excavation-permit-button--reject"
                  >
                    Reject
                  </Button>
                </span>
              </Tooltip>

              <Tooltip title={approveActionTooltip} arrow>
                <span style={{ display: "inline-flex" }}>
                  <Button
                    variant="contained"
                    onClick={openApprovePopup}
                    disabled={isApproveDisabled}
                    startIcon={<LuCircleCheck />}
                    className="excavation-permit-button excavation-permit-button--approve"
                  >
                    Approve
                  </Button>
                </span>
              </Tooltip>
            </Stack>
          </Paper>
        </CardContent>
      </Card>
    );
  };

  // footer is simplified: Cancel | Save inside department form, and Approve/Reject are above
  return (
    <div className={`data-list-content-container page-content excavation-review-page ${isSidebarExpanded ? "expanded" : "collapsed"}`}>
      <div className="data-form-content">
        <nav className="excavation-review-breadcrumb" aria-label="Breadcrumb">
          <button type="button" onClick={handleCancelClick}>Department review</button>
          <ChevronRight size={14} aria-hidden="true" />
          <span>Excavation permit</span>
          <ChevronRight size={14} aria-hidden="true" />
          <span aria-current="page">{department_name}</span>
        </nav>

        <div className="data-form-body-permit-excavation">
          <Box sx={{ width: "100%", mt: 1, display: "flex", flexDirection: "column", gap: 1.5 }}>
            {renderForm1()}
            {renderDepartmentForm()}
          </Box>
        </div>


        <Dialog
          open={showApprovePopup}
          onClose={() => !isLoading && closeApprovePopup()}
          fullWidth
          maxWidth="sm"
          PaperProps={{ sx: approvalDialogPaperSx }}
        >
          <DialogTitle sx={approvalDialogTitleSx}>
            <Box sx={{ ...dialogIconSx, bgcolor: "#dcfce7", color: "#15803d" }}>
              <LuCircleCheck />
            </Box>
            Approve excavation permit
          </DialogTitle>
          <DialogContent sx={{ pt: 1 }}>
            <Alert severity="success" sx={{ mb: 2 }}>
              Approval will submit this department review and move the permit forward.
            </Alert>
            <Typography sx={dialogLabelSx}>Remark</Typography>
            <textarea
              value={excavationRemark}
              onChange={(e) => setExcavationRemark(e.target.value)}
              placeholder="Enter approval remark"
              rows={4}
              style={dialogTextareaStyle}
            />
            <Typography sx={{ ...dialogLabelSx, mt: 2 }}>Attachment</Typography>
            <label style={fileUploadLabelStyle}>
              <LuUpload />
              <span>{excavationFile ? excavationFile.name : "Choose optional file"}</span>
              <input type="file" onChange={handleExcavationFileChange} style={{ display: "none" }} />
            </label>
          </DialogContent>
          <DialogActions sx={dialogActionsSx}>
            <Button onClick={closeApprovePopup} disabled={isLoading} className="excavation-permit-button">
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => doApproveOrReject("Approve")}
              disabled={isLoading}
              startIcon={
                isLoading ? <CircularProgress size={16} color="inherit" /> : <LuCircleCheck />
              }
              className="excavation-permit-button excavation-permit-button--approve"
            >
              {isLoading ? "Approving..." : "Approve Permit"}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={showRejectPopup}
          onClose={() => !isLoading && closeRejectPopup()}
          fullWidth
          maxWidth="sm"
          PaperProps={{ sx: approvalDialogPaperSx }}
        >
          <DialogTitle sx={approvalDialogTitleSx}>
            <Box sx={{ ...dialogIconSx, bgcolor: "#fee2e2", color: "#b91c1c" }}>
              <LuCircleX />
            </Box>
            Reject excavation permit
          </DialogTitle>
          <DialogContent sx={{ pt: 1 }}>

            <Typography sx={dialogLabelSx}>Rejection remark</Typography>
            <textarea
              value={excavationRemark}
              onChange={(e) => setExcavationRemark(e.target.value)}
              placeholder="Enter rejection reason"
              rows={4}
              style={dialogTextareaStyle}
            />
            <Typography sx={{ ...dialogLabelSx, mt: 2 }}>Attachment</Typography>
            <label style={fileUploadLabelStyle}>
              <LuUpload />
              <span>{excavationFile ? excavationFile.name : "Choose optional file"}</span>
              <input type="file" onChange={handleExcavationFileChange} style={{ display: "none" }} />
            </label>
          </DialogContent>
          <DialogActions sx={dialogActionsSx}>
            <Button onClick={closeRejectPopup} disabled={isLoading} className="excavation-permit-button">
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => doApproveOrReject("Reject")}
              disabled={isLoading}
              startIcon={
                isLoading ? <CircularProgress size={16} color="inherit" /> : <LuCircleX />
              }
              className="excavation-permit-button excavation-permit-button--reject"
            >
              {isLoading ? "Rejecting..." : "Reject Permit"}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleSnackbarClose} anchorOrigin={{ vertical: "top", horizontal: "right" }}>
          <MuiAlert onClose={handleSnackbarClose} severity={severity} variant="filled">{snackbarMessage}</MuiAlert>
        </Snackbar>
      </div>
    </div>
  );
}

const SafetyChoiceGroup = ({ title, description, icon: GroupIcon, choices, values, onChange, type = "checkbox", field }) => (
  <fieldset className="fire-safety-choice-group">
    <legend><GroupIcon size={18} aria-hidden="true" />{title}</legend>
    <p className="fire-safety-group-description">{description}</p>
    <span className="fire-safety-selection-hint">{type === "radio" ? "Select one" : "Select all that apply"}</span>
    <div className="fire-safety-choice-options">
      {choices.map((choice) => {
        const key = typeof choice === "string" ? choice : choice.field;
        const label = typeof choice === "string" ? choice : choice.label;
        const checked = type === "radio" ? values[field] === key : !!values[key];
        return (
          <label key={key} className={`fire-safety-choice${checked ? " is-selected" : ""}`}>
            <input
              type={type}
              name={type === "radio" ? field : undefined}
              checked={checked}
              onChange={(event) => onChange(type === "radio" ? field : key, type === "radio" ? key : event.target.checked)}
            />
            <span>{label}</span>
          </label>
        );
      })}
    </div>
  </fieldset>
);

const reviewDetailIcons = {
  "Permit Type": FileText,
  "Issued To Department": Building2,
  "Receiver Name": UserRound,
  "Work Location (Area / Unit)": MapPin,
  Section: Layers,
  "Work Description": FileText,
  "No. of Men": Users,
  "Start Date & Time": CalendarDays,
  "End Date & Time": CalendarDays,
  "Work Time From": Clock3,
  "Work Time To": Clock3,
  "Length (m)": Ruler,
  "Width (m)": MoveHorizontal,
  "Depth (m)": MoveVertical,
};

const reviewActionButtonBaseSx = {
  borderRadius: "8px",
  fontSize: "13px",
  fontWeight: 700,
  minHeight: "38px",
  px: 2,
  textTransform: "none",
  "&.Mui-disabled": {
    bgcolor: "#e5e7eb",
    borderColor: "#e5e7eb",
    color: "#94a3b8",
    cursor: "not-allowed",
  },
};

const reviewActionButtonSx = {
  save: {
    ...reviewActionButtonBaseSx,
    bgcolor: "#2563eb",
    boxShadow: "0 8px 18px rgba(37, 99, 235, 0.2)",
    color: "#ffffff",
    "&:hover": {
      bgcolor: "#1d4ed8",
      boxShadow: "0 10px 22px rgba(37, 99, 235, 0.24)",
    },
  },

};

const approvalDialogPaperSx = {
  borderRadius: "14px",
  boxShadow: "0 24px 70px rgba(15, 23, 42, 0.28)",
};

const approvalDialogTitleSx = {
  alignItems: "center",
  color: "#172033",
  display: "flex",
  fontSize: "18px",
  fontWeight: 800,
  gap: 1.2,
  pb: 1,
};

const dialogIconSx = {
  alignItems: "center",
  borderRadius: "50%",
  display: "inline-flex",
  flex: "0 0 36px",
  height: 36,
  justifyContent: "center",
  width: 36,
  "& svg": {
    fontSize: 20,
  },
};

const dialogLabelSx = {
  color: "#334155",
  fontSize: "13px",
  fontWeight: 700,
  mb: 0.75,
};

const dialogTextareaStyle = {
  border: "1px solid #cbd5e1",
  borderRadius: 10,
  boxSizing: "border-box",
  fontSize: 14,
  lineHeight: 1.5,
  outline: "none",
  padding: "10px 12px",
  resize: "vertical",
  width: "100%",
};

const fileUploadLabelStyle = {
  alignItems: "center",
  border: "1px dashed #94a3b8",
  borderRadius: 10,
  boxSizing: "border-box",
  color: "#334155",
  cursor: "pointer",
  display: "flex",
  fontSize: 13,
  fontWeight: 700,
  gap: 8,
  minHeight: 42,
  padding: "10px 12px",
  width: "100%",
};

const dialogActionsSx = {
  borderTop: "1px solid #eef2f7",
  gap: 1,
  px: 3,
  py: 2,
};








// ---- correct ---

// import React, { useState, useEffect } from "react";
// import { useNavigate, useParams } from "react-router-dom";
// import axios from "axios";
// import Snackbar from "@mui/material/Snackbar";
// import MuiAlert from "@mui/material/Alert";
// import {
//   Box,
//   Card,
//   CardContent,
//   Divider,
//   Typography,
//   CircularProgress,
//   Chip,
//   Stack,
//   Paper,
//   Button,
//   Radio,
// } from "@mui/material";
// import "react-datepicker/dist/react-datepicker.css";
// import API_URL from "../../src/Config";
// import { IoArrowBackCircle } from "react-icons/io5";

// function ExcavationPermitViewPage({
//   isSidebarExpanded,
//   fetchComplitionSatusData,
// }) {
//   const navigate = useNavigate();
//   const [isLoading, setIsLoading] = useState(false);
//   const [snackbarMessage, setSnackbarMessage] = useState("");
//   const [severity, setSeverity] = useState("success");
//   const [snackbarOpen, setSnackbarOpen] = useState(false);
//   const {
//     permit_id,
//     department_name,
//     permit_type_id,
//     work_permit_name,
//     wp_status,
//   } = useParams();

//   const [excavationWorkPermit, setExcavationWorkPermit] = useState("");
//   const [form1Data, setForm1Data] = useState({
//     workPermitName: "",
//     formData: {},
//     dateValues: {},
//     equipmentDetails: [],
//   });

//   // Department observations fetched from backend
//   const [departmentObservations, setDepartmentObservations] = useState([]);

//   // Local editable state for current department form
//   const [departmentFormData, setDepartmentFormData] = useState({
//     observations: "",
//   });

//   const [departmentInfo, setDepartmentInfo] = useState(null);

//   console.log("permit_id", permit_id);
//   console.log("department_name from params", department_name);

//   // Utility Functions
//   const showSnackbar = (message, type = "success") => {
//     setSnackbarMessage(message);
//     setSeverity(type);
//     setSnackbarOpen(true);
//   };

//   const handleSnackbarClose = (event, reason) => {
//     if (reason === "clickaway") return;
//     setSnackbarOpen(false);
//   };

//   const handleCancelClick = () => {
//     navigate("/excavation-department-review");
//   };

//   const getDepartmentPendingPermit = async () => {
//     try {
//       const authKey = localStorage.getItem("authKey");
//       const response = await axios.get(
//         `${API_URL}/api/department/pending_permits`,
//         { headers: { Authorization: authKey } }
//       );

//       const responseData = response?.data?.permits[0];
//       console.log("responseData", responseData);
//     } catch (error) {
//       console.error("Failed to load permit details:", error);
//       showSnackbar("Failed to load permit details", "error");
//     }
//   };

//   // Fetch work permit details
//   const fetchPermitDetails = async () => {
//     try {
//       const authKey = localStorage.getItem("authKey");
//       const response = await axios.get(
//         `${API_URL}/api/get_permit_work_details/${permit_id}`,
//         { headers: { Authorization: authKey } }
//       );

//       const permit = response.data?.permit_details?.[0];
//       if (!permit) {
//         console.warn("No permit details found");
//         return;
//       }

//       setExcavationWorkPermit(permit?.work_permit_name);

//       const formatDate = (dateString) => {
//         if (!dateString) return null;
//         const date = new Date(dateString);
//         date.setMinutes(date.getMinutes() + date.getTimezoneOffset());
//         return date;
//       };

//       const dependentFieldValues = {};
//       const dependentFieldsFromPermit =
//         permit.issued_to?.dependent_fields || [];
//       dependentFieldsFromPermit.forEach((dep) => {
//         dependentFieldValues[dep.field_label] = dep.value;
//       });

//       setForm1Data({
//         workPermitName: permit?.work_permit_name || "",
//         formData: {
//           permit_type_id: permit?.permit_type_id || "",
//           issued_to: permit?.issued_to?.value || "",
//           ...dependentFieldValues,
//           department: permit?.department_name || "",
//           receiver_name: permit?.receiver_name || "",
//           contractor: permit?.contractor || "",
//           section: permit?.section || "",
//           area: permit?.area || "",
//           unit: permit?.unit || "",
//           work_description: permit?.work_description || "",
//           work_type: permit?.work_type || "",
//           related_permit_no: permit?.related_permit_no || "",
//           no_of_men: permit?.no_of_men || "",
//           work_location: permit?.work_location || "",
//           facility_location: permit?.facility_location || "",
//           excavation_length: permit?.excavation_length || "",
//           excavation_width: permit?.excavation_width || "",
//           excavation_depth: permit?.excavation_depth || "",
//           created_at: permit?.created_at || "",
//           updated_at: permit?.updated_at || "",
//         },
//         dateValues: {
//           "Start Date & Time": permit.valid_from
//             ? formatDate(permit.valid_from)
//             : null,
//           "End Date & Time": permit.valid_to
//             ? formatDate(permit.valid_to)
//             : null,
//           "Work Time From": permit.work_time_from
//             ? formatDate(permit.work_time_from)
//             : null,
//           "Work Time To": permit.work_time_to
//             ? formatDate(permit.work_time_to)
//             : null,
//         },
//         equipmentDetails: permit?.equipment_details || [],
//       });
//     } catch (error) {
//       console.error("Failed to load permit details:", error);
//       showSnackbar("Failed to load permit details", "error");
//     }
//   };

//   // Fetch department observations for current user's department
//   const fetchDepartmentObservations = async () => {
//     try {
//       const authKey = localStorage.getItem("authKey");
//       const response = await axios.get(
//         `${API_URL}/api/permit/department/my-observations/${permit_id}`,
//         { headers: { Authorization: authKey } }
//       );

//       const observations = response.data?.approvals || [];
//       setDepartmentObservations(observations);
//       console.log("observations", observations);

//       // Find the observation matching the department_name from params
//       const matchingObs = observations.find(
//         (obs) =>
//           obs.department_name?.toLowerCase() === department_name?.toLowerCase()
//       );

//       if (matchingObs) {
//         const formData = { observations: matchingObs.observations || "" };

//         // Convert inputs array to object
//         matchingObs.inputs?.forEach((inp) => {
//           const value =
//             inp.field_type === "boolean"
//               ? inp.field_value === "true" || inp.field_value === true
//               : inp.field_value || "";
//           formData[inp.field_name] = value;
//         });

//         setDepartmentFormData(formData);
//         setDepartmentInfo({
//           approval_id: matchingObs.approval_id,
//           department_id: matchingObs.department_id,
//           department_name: matchingObs.department_name,
//           can_approve: matchingObs.can_approve,
//           can_reject: matchingObs.can_reject,
//         });
//       }
//     } catch (error) {
//       console.error("Failed to load department observations:", error);
//       if (error.response?.status !== 403) {
//         showSnackbar("Failed to load department observations", "error");
//       }
//     }
//   };

//   useEffect(() => {
//     if (permit_id && department_name) {
//       fetchPermitDetails();
//       fetchDepartmentObservations();
//       getDepartmentPendingPermit();
//     }
//   }, [permit_id, department_name]);

//   // Handle form field changes
//   const handleFieldChange = (fieldName, value) => {
//     setDepartmentFormData((prev) => ({
//       ...prev,
//       [fieldName]: value,
//     }));
//   };

//   // Save department observations
//   const handleSaveDepartment = async () => {
//     if (!departmentInfo) {
//       showSnackbar("Department information not found", "error");
//       return;
//     }

//     setIsLoading(true);
//     try {
//       const authKey = localStorage.getItem("authKey");

//       // Convert form data to inputs array
//       const inputs = [];
//       Object.entries(departmentFormData).forEach(([key, value]) => {
//         if (key !== "observations") {
//           inputs.push({
//             field_name: key,
//             field_value: typeof value === "boolean" ? value.toString() : value,
//             field_type:
//               typeof value === "boolean"
//                 ? "boolean"
//                 : typeof value === "number"
//                 ? "number"
//                 : "text",
//           });
//         }
//       });

//       const payload = {
//         permit_id: permit_id,
//         department_id: departmentInfo.department_id,
//         observations: departmentFormData.observations || "",
//         inputs: inputs,
//       };

//       const response = await axios.post(
//         `${API_URL}/api/permit/department/observations`,
//         payload,
//         {
//           headers: {
//             Authorization: authKey,
//             "Content-Type": "application/json",
//           },
//         }
//       );

//       if (response.status === 200) {
//         showSnackbar("Department observations saved successfully!", "success");
//         fetchDepartmentObservations();
//         fetchComplitionSatusData && fetchComplitionSatusData();
//       }
//     } catch (error) {
//       console.error("Error saving department observations:", error);
//       showSnackbar(
//         error.response?.data?.message ||
//           "Error saving department observations.",
//         "error"
//       );
//     } finally {
//       setIsLoading(false);
//     }
//   };

//   // Render work permit details
//   const renderForm1 = () => {
//     const permit = form1Data?.formData || {};
//     const dateValues = form1Data?.dateValues || {};

//     const formatDateTime = (dateString) => {
//       if (!dateString) return "-";
//       const date = new Date(dateString);
//       return date.toLocaleString("en-IN", {
//         day: "2-digit",
//         month: "short",
//         year: "numeric",
//         hour: "2-digit",
//         minute: "2-digit",
//       });
//     };

//     return (
//       <Card
//         sx={{
//           mb: 3,
//           borderRadius: 3,
//           boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
//           overflow: "hidden",
//         }}
//       >
//         <CardContent sx={{ p: 3 }}>
//           <Typography
//             variant="h6"
//             sx={{
//               mb: 2,
//               fontWeight: 600,
//               color: "primary.main",
//               borderBottom: "2px solid",
//               borderColor: "primary.light",
//               pb: 1,
//               fontSize: "1.1rem",
//             }}
//           >
//             Work Permit Details
//           </Typography>

//           <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap">
//             {permit?.work_type && (
//               <Chip
//                 label={permit?.work_type}
//                 color="primary"
//                 variant="outlined"
//                 size="small"
//               />
//             )}
//             {permit?.contractor && (
//               <Chip
//                 label={`Contractor: ${permit?.contractor}`}
//                 color="secondary"
//                 size="small"
//               />
//             )}
//             {permit?.department && (
//               <Chip
//                 label={`Dept: ${permit?.department}`}
//                 color="default"
//                 size="small"
//               />
//             )}
//           </Stack>

//           <Box
//             sx={{
//               display: "grid",
//               gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
//               gap: 2,
//             }}
//           >
//             <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
//               <Typography
//                 variant="subtitle2"
//                 sx={{
//                   fontWeight: 600,
//                   mb: 1.5,
//                   color: "text.secondary",
//                   fontSize: "0.85rem",
//                 }}
//               >
//                 General Information
//               </Typography>
//               <Stack spacing={1}>
//                 <DetailItem
//                   label="Permit Type"
//                   value={form1Data?.workPermitName}
//                 />
//                 <DetailItem label="Issued To" value={permit?.issued_to} />
//                 <DetailItem
//                   label="Receiver Name"
//                   value={permit?.receiver_name}
//                 />
//                 <DetailItem label="Area" value={permit?.area} />
//                 <DetailItem label="Section" value={permit?.section} />
//                 <DetailItem
//                   label="Work Location"
//                   value={permit?.work_location}
//                 />

//                 {(permit?.excavation_length ||
//                   permit?.excavation_width ||
//                   permit?.excavation_depth) && (
//                   <Box
//                     sx={{ mt: 2, p: 1.5, bgcolor: "grey.50", borderRadius: 1 }}
//                   >
//                     <Typography
//                       variant="subtitle2"
//                       sx={{ fontWeight: 600, mb: 1, fontSize: "0.85rem" }}
//                     >
//                       Excavation Details
//                     </Typography>
//                     <Stack direction="row" spacing={2} flexWrap="wrap">
//                       {permit?.excavation_length && (
//                         <DetailItem
//                           label="Length"
//                           value={permit.excavation_length}
//                         />
//                       )}
//                       {permit?.excavation_width && (
//                         <DetailItem
//                           label="Width"
//                           value={permit.excavation_width}
//                         />
//                       )}
//                       {permit?.excavation_depth && (
//                         <DetailItem
//                           label="Depth"
//                           value={permit.excavation_depth}
//                         />
//                       )}
//                     </Stack>
//                   </Box>
//                 )}
//               </Stack>
//             </Paper>

//             <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
//               <Typography
//                 variant="subtitle2"
//                 sx={{
//                   fontWeight: 600,
//                   mb: 1.5,
//                   color: "text.secondary",
//                   fontSize: "0.85rem",
//                 }}
//               >
//                 Work Details
//               </Typography>
//               <Stack spacing={1}>
//                 <DetailItem
//                   label="Work Description"
//                   value={permit?.work_description}
//                 />
//                 <DetailItem label="No. of Men" value={permit?.no_of_men} />
//                 <DetailItem
//                   label="Start Date & Time"
//                   value={formatDateTime(dateValues["Start Date & Time"])}
//                 />
//                 <DetailItem
//                   label="End Date & Time"
//                   value={formatDateTime(dateValues["End Date & Time"])}
//                 />
//                 <DetailItem
//                   label="Work Time From"
//                   value={formatDateTime(dateValues["Work Time From"])}
//                 />
//                 <DetailItem
//                   label="Work Time To"
//                   value={formatDateTime(dateValues["Work Time To"])}
//                 />
//               </Stack>
//             </Paper>
//           </Box>
//         </CardContent>
//       </Card>
//     );
//   };

//   const DetailItem = ({ label, value }) => (
//     <Box>
//       <Typography
//         variant="caption"
//         color="textSecondary"
//         sx={{ fontSize: "0.75rem" }}
//       >
//         {label}:
//       </Typography>
//       <Typography
//         variant="body2"
//         sx={{ fontWeight: 500, color: "text.primary", fontSize: "0.85rem" }}
//       >
//         {value || "—"}
//       </Typography>
//     </Box>
//   );

//   // Render department form based on department_name from params
//   const renderDepartmentForm = () => {
//     if (!department_name) {
//       return (
//         <Paper sx={{ p: 3, textAlign: "center" }}>
//           <Typography color="textSecondary">No department specified</Typography>
//         </Paper>
//       );
//     }

//     return (
//       <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
//         <CardContent sx={{ p: 3 }}>
//           <Typography
//             variant="h6"
//             sx={{ mb: 2, fontWeight: 600, color: "primary.main" }}
//           >
//             {department_name}
//           </Typography>

//           {renderDepartmentSpecificFields()}

//           <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
//             <Button
//               variant="contained"
//               onClick={handleSaveDepartment}
//               disabled={isLoading || !departmentInfo}
//               sx={{ backgroundColor: "green" }}
//             >
//               {isLoading ? (
//                 <CircularProgress size={20} color="inherit" />
//               ) : (
//                 "Save"
//               )}
//             </Button>
//           </Box>
//         </CardContent>
//       </Card>
//     );
//   };

//   const renderDepartmentSpecificFields = () => {
//     const TextInput = ({ label, fieldName, type = "text" }) => (
//       <Box sx={{ mb: 2 }}>
//         <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
//           {label}
//         </Typography>
//         <input
//           type={type}
//           value={departmentFormData[fieldName] || ""}
//           onChange={(e) => handleFieldChange(fieldName, e.target.value)}
//           style={{
//             width: "100%",
//             padding: "8px 10px",
//             borderRadius: "4px",
//             border: "1px solid #ccc",
//             fontSize: "14px",
//           }}
//         />
//       </Box>
//     );

//     const TextArea = ({ label, fieldName, rows = 3 }) => (
//       <Box sx={{ mb: 2 }}>
//         <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
//           {label}
//         </Typography>
//         <textarea
//           value={departmentFormData[fieldName] || ""}
//           onChange={(e) => handleFieldChange(fieldName, e.target.value)}
//           rows={rows}
//           style={{
//             width: "100%",
//             padding: "8px 10px",
//             borderRadius: "4px",
//             border: "1px solid #ccc",
//             fontSize: "14px",
//           }}
//         />
//       </Box>
//     );

//     const Checkbox = ({ label, fieldName }) => (
//       <label
//         style={{
//           display: "inline-flex",
//           alignItems: "center",
//           marginRight: "16px",
//           cursor: "pointer",
//         }}
//       >
//         <input
//           type="checkbox"
//           checked={departmentFormData[fieldName] || false}
//           onChange={(e) => handleFieldChange(fieldName, e.target.checked)}
//           style={{ marginRight: "6px" }}
//         />
//         <Typography variant="body2">{label}</Typography>
//       </label>
//     );

//     const deptNameLower = department_name?.toLowerCase() || "";
//     console.log("Department name for rendering:", department_name);
//     console.log("Department name lowercase:", deptNameLower);

//     // Render fields based on department_name from params - order matters, check mechanical first
//     if (deptNameLower.includes("mechanical")) {
//       return (
//         <>
//           <TextArea
//             label="Description of underground facilities existing at the proposed location"
//             fieldName="underground_facilities_description"
//           />
//           <TextArea
//             label="Precautions to be observed for safe excavation job"
//             fieldName="precautions_safe_excavation"
//           />
//           <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
//             <TextInput label="Date" fieldName="date" type="date" />
//             <TextInput
//               label="Name & Designation"
//               fieldName="name_designation"
//             />
//             <TextInput label="Signature" fieldName="signature" />
//           </Box>
//         </>
//       );
//     }

//     if (
//       deptNameLower.includes("electrician") ||
//       deptNameLower.includes("electrical") ||
//       deptNameLower.includes("electician")
//     ) {
//       return (
//         <>
//           <TextArea
//             label="Details of underground cables present in the area to be excavated"
//             fieldName="underground_cables_details"
//           />
//           <TextArea
//             label="Electrical isolation required"
//             fieldName="electrical_isolation_required"
//           />
//           <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
//             <TextInput label="Date" fieldName="date" type="date" />
//             <TextInput
//               label="Name & Designation"
//               fieldName="name_designation"
//             />
//             <TextInput label="Signature" fieldName="signature" />
//           </Box>
//         </>
//       );
//     }

//     if (deptNameLower.includes("instrument")) {
//       return (
//         <>
//           <TextArea
//             label="Details of underground cables present in the area to be excavated"
//             fieldName="underground_cables_details"
//           />
//           <TextArea
//             label="Precautions to be observed for safe excavation job"
//             fieldName="precautions_safe_excavation"
//           />
//           <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
//             <TextInput label="Date" fieldName="date" type="date" />
//             <TextInput
//               label="Name & Designation"
//               fieldName="name_designation"
//             />
//             <TextInput label="Signature" fieldName="signature" />
//           </Box>
//         </>
//       );
//     }

//     if (deptNameLower.includes("fire") || deptNameLower.includes("safety")) {
//       return (
//         <>
//           <Box sx={{ mb: 2 }}>
//             <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
//               Provide:
//             </Typography>
//             <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
//               <Checkbox label="Cross over" fieldName="provide_cross_over" />
//               <Checkbox label="Barricades" fieldName="provide_barricades" />
//               <Checkbox label="Road blocks" fieldName="provide_road_blocks" />
//               <Checkbox label="Shoring" fieldName="provide_shoring" />
//               <Checkbox label="Others" fieldName="others" />
//             </Box>
//           </Box>

//           <Box sx={{ mb: 2 }}>
//             <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
//               Types of excavation
//             </Typography>
//             <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
//               <Radio
//                 label="Manual"
//                 fieldName="emergency_approach_for_vehicles"
//               />
//               <Radio
//                 label="Machenical"
//                 fieldName="emergency_approach_barricading"
//               />
//               <Radio
//                 label="Hand tools"
//                 fieldName="emergency_approach_road_closure"
//               />
//               <Radio
//                 label="Others"
//                 fieldName="emergency_approach_road_closure"
//               />
//             </Box>
//           </Box>

//           <Box sx={{ mb: 2 }}>
//             <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
//               Use following safety equipment:
//             </Typography>
//             <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
//               <Checkbox
//                 label="Gas Masks"
//                 fieldName="safety_equipment_gas_masks"
//               />
//               <Checkbox
//                 label="Eye Protection"
//                 fieldName="safety_equipment_eye_protection"
//               />
//               <Checkbox
//                 label="Ear Protection"
//                 fieldName="safety_equipment_ear_protection"
//               />
//               <Checkbox
//                 label="Wooden Handheld tools"
//                 fieldName="safety_equipment_wooden_handheld"
//               />
//               <Checkbox label="Others" fieldName="others" />
//             </Box>
//           </Box>

//           <Box sx={{ mb: 2 }}>
//             <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
//               Provide following:
//             </Typography>
//             <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
//               <Checkbox
//                 label="Emergency approach for vehicles"
//                 fieldName="emergency_approach_for_vehicles"
//               />
//               <Checkbox
//                 label="Barricading"
//                 fieldName="emergency_approach_barricading"
//               />
//               <Checkbox
//                 label="Road closure"
//                 fieldName="emergency_approach_road_closure"
//               />
//             </Box>
//           </Box>

//           <TextArea
//             label="Other precautions"
//             fieldName="other_precautions"
//             rows={3}
//           />
//         </>
//       );
//     }

//     return (
//       <Typography color="textSecondary">
//         No specific fields configured for this department: {department_name}
//       </Typography>
//     );
//   };

//   const handleGenratePermit = () => {
//     const encodedPermitName = encodeURIComponent(excavationWorkPermit);
//     navigate(
//       `/view-pdf-formate/${permit_id}/${encodedPermitName}/${
//         wp_status || "wp_E&R"
//       }`
//     );
//   };

//   return (
//     <div
//       className={`data-list-content-container page-content ${
//         isSidebarExpanded ? "expanded" : "collapsed"
//       }`}
//     >
//       <div className="data-form-content">
//         <div className="data-form-header">
//           <div className="data-form-header-content">
//             <div className="data-form-heading-content-left-side">
//               <IoArrowBackCircle
//                 style={{
//                   color: "#233b80",
//                   paddingTop: "3px",
//                   cursor: "pointer",
//                 }}
//                 size={25}
//                 onClick={handleCancelClick}
//               />
//               <span className="data-form-heading-text">
//                 Excavation Clearance / Road Closure Work Permit View -{" "}
//                 {department_name}
//               </span>
//             </div>
//           </div>
//         </div>
//         <Divider />

//         <div className="data-form-body-permit-fands">
//           <Box
//             sx={{
//               width: "100%",
//               mt: 2,
//               display: "flex",
//               flexDirection: "column",
//               gap: 2,
//             }}
//           >
//             {renderForm1()}
//             {renderDepartmentForm()}
//           </Box>
//         </div>

//         <div className="footer">
//           <div className="button-group" style={{ display: "flex", gap: 8 }}>
//             <Button variant="outlined" onClick={handleCancelClick}>
//               Cancel
//             </Button>
//             <Button
//               variant="contained"
//               onClick={handleGenratePermit}
//               sx={{ backgroundColor: "#408dfb" }}
//             >
//               View PDF
//             </Button>
//           </div>
//         </div>

//         <Snackbar
//           open={snackbarOpen}
//           autoHideDuration={6000}
//           onClose={handleSnackbarClose}
//           anchorOrigin={{ vertical: "top", horizontal: "right" }}
//         >
//           <MuiAlert
//             onClose={handleSnackbarClose}
//             severity={severity}
//             variant="filled"
//           >
//             {snackbarMessage}
//           </MuiAlert>
//         </Snackbar>
//       </div>
//     </div>
//   );
// }

// export default ExcavationPermitViewPage;

















// import React, { useState, useEffect } from "react";
// import { useNavigate, useParams } from "react-router-dom";
// import axios from "axios";
// import Snackbar from "@mui/material/Snackbar";
// import MuiAlert from "@mui/material/Alert";
// import {
//   Box,
//   Card,
//   CardContent,
//   Divider,
//   Typography,
//   CircularProgress,
//   Chip,
//   Stack,
//   Paper,
//   Button,
//   Dialog,
//   DialogTitle,
//   DialogContent,
//   DialogActions,
//   TextField,
// } from "@mui/material";
// import "react-datepicker/dist/react-datepicker.css";
// import API_URL from "../../src/Config";
// import { IoArrowBackCircle } from "react-icons/io5";

// function ExcavationPermitViewPage({
//   isSidebarExpanded,
//   fetchComplitionSatusData,
// }) {
//   const navigate = useNavigate();
//   const [isLoading, setIsLoading] = useState(false);
//   const [snackbarMessage, setSnackbarMessage] = useState("");
//   const [severity, setSeverity] = useState("success");
//   const [snackbarOpen, setSnackbarOpen] = useState(false);
//   const { permit_id } = useParams();

//   const [excavationWorkPermit, setExcavationWorkPermit] = useState("");
//   const [form1Data, setForm1Data] = useState({
//     workPermitName: "",
//     formData: {},
//     dateValues: {},
//     equipmentDetails: [],
//   });

//   // Department info from pending_permits API
//   const [reviewDepartment, setReviewDepartment] = useState(null);
//   const [departmentFormData, setDepartmentFormData] = useState({
//     observations: "",
//   });

//   // Approval/Reject popup states
//   const [showApprovePopup, setShowApprovePopup] = useState(false);
//   const [showRejectPopup, setShowRejectPopup] = useState(false);
//   const [remark, setRemark] = useState("");
//   const [attachmentFile, setAttachmentFile] = useState(null);

//   // Can approve/reject flags
//   const [canApprove, setCanApprove] = useState(false);
//   const [canReject, setCanReject] = useState(false);

//   // Utility Functions
//   const showSnackbar = (message, type = "success") => {
//     setSnackbarMessage(message);
//     setSeverity(type);
//     setSnackbarOpen(true);
//   };

//   const handleSnackbarClose = (event, reason) => {
//     if (reason === "clickaway") return;
//     setSnackbarOpen(false);
//   };

//   const handleCancelClick = () => {
//     navigate("/excavation-department-review");
//   };

//   // Fetch work permit details
//   const fetchPermitDetails = async () => {
//     try {
//       const authKey = localStorage.getItem("authKey");
//       const response = await axios.get(
//         `${API_URL}/api/get_permit_work_details/${permit_id}`,
//         { headers: { Authorization: authKey } }
//       );

//       const permit = response.data?.permit_details?.[0];
//       if (!permit) {
//         console.warn("No permit details found");
//         return;
//       }

//       setExcavationWorkPermit(permit?.work_permit_name);

//       const formatDate = (dateString) => {
//         if (!dateString) return null;
//         const date = new Date(dateString);
//         date.setMinutes(date.getMinutes() + date.getTimezoneOffset());
//         return date;
//       };

//       const dependentFieldValues = {};
//       const dependentFieldsFromPermit =
//         permit.issued_to?.dependent_fields || [];
//       dependentFieldsFromPermit.forEach((dep) => {
//         dependentFieldValues[dep.field_label] = dep.value;
//       });

//       setForm1Data({
//         workPermitName: permit?.work_permit_name || "",
//         formData: {
//           permit_type_id: permit?.permit_type_id || "",
//           issued_to: permit?.issued_to?.value || "",
//           ...dependentFieldValues,
//           department: permit?.department_name || "",
//           receiver_name: permit?.receiver_name || "",
//           contractor: permit?.contractor || "",
//           section: permit?.section || "",
//           area: permit?.area || "",
//           unit: permit?.unit || "",
//           work_description: permit?.work_description || "",
//           work_type: permit?.work_type || "",
//           related_permit_no: permit?.related_permit_no || "",
//           no_of_men: permit?.no_of_men || "",
//           work_location: permit?.work_location || "",
//           facility_location: permit?.facility_location || "",
//           excavation_length: permit?.excavation_length || "",
//           excavation_width: permit?.excavation_width || "",
//           excavation_depth: permit?.excavation_depth || "",
//           created_at: permit?.created_at || "",
//           updated_at: permit?.updated_at || "",
//         },
//         dateValues: {
//           "Start Date & Time": permit.valid_from
//             ? formatDate(permit.valid_from)
//             : null,
//           "End Date & Time": permit.valid_to
//             ? formatDate(permit.valid_to)
//             : null,
//           "Work Time From": permit.work_time_from
//             ? formatDate(permit.work_time_from)
//             : null,
//           "Work Time To": permit.work_time_to
//             ? formatDate(permit.work_time_to)
//             : null,
//         },
//         equipmentDetails: permit?.equipment_details || [],
//       });
//     } catch (error) {
//       console.error("Failed to load permit details:", error);
//       showSnackbar("Failed to load permit details", "error");
//     }
//   };

//   // Fetch pending permits to get review_department
//   const getDepartmentPendingPermit = async () => {
//     try {
//       const authKey = localStorage.getItem("authKey");
//       const response = await axios.get(
//         `${API_URL}/api/department/pending_permits`,
//         { headers: { Authorization: authKey } }
//       );

//       const permits = response?.data?.permits || [];
//       const currentPermit = permits.find(
//         (p) => p.permit_id?.toString() === permit_id?.toString()
//       );

//       if (currentPermit && currentPermit.review_department) {
//         setReviewDepartment(currentPermit.review_department);
//         setCanApprove(currentPermit.can_approve || false);
//         setCanReject(currentPermit.can_reject || false);
//       } else {
//         console.warn("No review department found for this permit");
//       }
//     } catch (error) {
//       console.error("Failed to load pending permits:", error);
//       showSnackbar("Failed to load pending permits", "error");
//     }
//   };

//   // Fetch department observations for prefilling form
//   const fetchDepartmentObservations = async () => {
//     try {
//       const authKey = localStorage.getItem("authKey");
//       const response = await axios.get(
//         `${API_URL}/api/permit/department/my-observations/${permit_id}`,
//         { headers: { Authorization: authKey } }
//       );

//       const observations = response.data?.approvals || [];

//       if (observations.length > 0) {
//         const obs = observations[0];
//         const formData = { observations: obs.observations || "" };

//         obs.inputs?.forEach((inp) => {
//           const value =
//             inp.field_type === "boolean"
//               ? inp.field_value === "true" || inp.field_value === true
//               : inp.field_value || "";
//           formData[inp.field_name] = value;
//         });

//         setDepartmentFormData(formData);
//       }
//     } catch (error) {
//       console.error("Failed to load department observations:", error);
//       if (error.response?.status !== 404) {
//         // Don't show error for 404 as it means no observations yet
//       }
//     }
//   };

//   useEffect(() => {
//     if (permit_id) {
//       fetchPermitDetails();
//       getDepartmentPendingPermit();
//       fetchDepartmentObservations();
//     }
//   }, [permit_id]);

//   // Handle form field changes
//   const handleFieldChange = (fieldName, value) => {
//     setDepartmentFormData((prev) => ({
//       ...prev,
//       [fieldName]: value,
//     }));
//   };

//   // Save department observations
//   const handleSaveDepartment = async () => {
//     if (!reviewDepartment) {
//       showSnackbar("Department information not found", "error");
//       return;
//     }

//     setIsLoading(true);
//     try {
//       const authKey = localStorage.getItem("authKey");

//       const inputs = [];
//       Object.entries(departmentFormData).forEach(([key, value]) => {
//         if (key !== "observations") {
//           inputs.push({
//             field_name: key,
//             field_value: typeof value === "boolean" ? value.toString() : value,
//             field_type:
//               typeof value === "boolean"
//                 ? "boolean"
//                 : typeof value === "number"
//                 ? "number"
//                 : "text",
//           });
//         }
//       });

//       const payload = {
//         permit_id: permit_id,
//         department_id: reviewDepartment.department_id,
//         observations: departmentFormData.observations || "",
//         inputs: inputs,
//       };

//       const response = await axios.post(
//         `${API_URL}/api/permit/department/observations`,
//         payload,
//         {
//           headers: {
//             Authorization: authKey,
//             "Content-Type": "application/json",
//           },
//         }
//       );

//       if (response.status === 200) {
//         showSnackbar("Department observations saved successfully!", "success");
//         fetchDepartmentObservations();
//         fetchComplitionSatusData && fetchComplitionSatusData();
//       }
//     } catch (error) {
//       console.error("Error saving department observations:", error);
//       showSnackbar(
//         error.response?.data?.message ||
//           "Error saving department observations.",
//         "error"
//       );
//     } finally {
//       setIsLoading(false);
//     }
//   };

//   // Approve handler
//   const handleApprove = async () => {
//     if (!reviewDepartment) {
//       showSnackbar("Department information not found", "error");
//       return;
//     }

//     setIsLoading(true);
//     try {
//       const authKey = localStorage.getItem("authKey");
//       const formData = new FormData();

//       formData.append("permit_id", permit_id);
//       formData.append("department_id", reviewDepartment.department_id);
//       formData.append("action", "Approve");
//       formData.append("remarks", remark || "");

//       if (attachmentFile) {
//         formData.append("attachments", attachmentFile);
//       }

//       const response = await axios.post(
//         `${API_URL}/api/permit/department/approve`,
//         formData,
//         {
//           headers: {
//             Authorization: authKey,
//             "Content-Type": "multipart/form-data",
//           },
//         }
//       );

//       showSnackbar(
//         response.data.message || "Permit approved successfully!",
//         "success"
//       );
//       setShowApprovePopup(false);
//       setTimeout(() => navigate("/excavation-department-review"), 1500);
//     } catch (error) {
//       console.error("Error approving permit:", error);
//       showSnackbar(
//         error.response?.data?.message ||
//           "Failed to approve permit. Please try again.",
//         "error"
//       );
//     } finally {
//       setIsLoading(false);
//     }
//   };

//   // Reject handler
//   const handleReject = async () => {
//     if (!reviewDepartment) {
//       showSnackbar("Department information not found", "error");
//       return;
//     }

//     setIsLoading(true);
//     try {
//       const authKey = localStorage.getItem("authKey");
//       const formData = new FormData();

//       formData.append("permit_id", permit_id);
//       formData.append("department_id", reviewDepartment.department_id);
//       formData.append("action", "Reject");
//       formData.append("remarks", remark || "");

//       if (attachmentFile) {
//         formData.append("attachments", attachmentFile);
//       }

//       const response = await axios.post(
//         `${API_URL}/api/permit/department/approve`,
//         formData,
//         {
//           headers: {
//             Authorization: authKey,
//             "Content-Type": "multipart/form-data",
//           },
//         }
//       );

//       showSnackbar(
//         response.data.message || "Permit rejected successfully!",
//         "success"
//       );
//       setShowRejectPopup(false);
//       setTimeout(() => navigate("/excavation-department-review"), 1500);
//     } catch (error) {
//       console.error("Error rejecting permit:", error);
//       showSnackbar(
//         error.response?.data?.message ||
//           "Failed to reject permit. Please try again.",
//         "error"
//       );
//     } finally {
//       setIsLoading(false);
//     }
//   };

//   // Render work permit details
//   const renderForm1 = () => {
//     const permit = form1Data?.formData || {};
//     const dateValues = form1Data?.dateValues || {};

//     const formatDateTime = (dateString) => {
//       if (!dateString) return "-";
//       const date = new Date(dateString);
//       return date.toLocaleString("en-IN", {
//         day: "2-digit",
//         month: "short",
//         year: "numeric",
//         hour: "2-digit",
//         minute: "2-digit",
//       });
//     };

//     const DetailItem = ({ label, value }) => (
//       <Box>
//         <Typography
//           variant="caption"
//           color="textSecondary"
//           sx={{ fontSize: "0.75rem" }}
//         >
//           {label}:
//         </Typography>
//         <Typography
//           variant="body2"
//           sx={{ fontWeight: 500, color: "text.primary", fontSize: "0.85rem" }}
//         >
//           {value || "—"}
//         </Typography>
//       </Box>
//     );

//     return (
//       <Card
//         sx={{
//           mb: 3,
//           borderRadius: 3,
//           boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
//           overflow: "hidden",
//         }}
//       >
//         <CardContent sx={{ p: 3 }}>
//           <Typography
//             variant="h6"
//             sx={{
//               mb: 2,
//               fontWeight: 600,
//               color: "primary.main",
//               borderBottom: "2px solid",
//               borderColor: "primary.light",
//               pb: 1,
//               fontSize: "1.1rem",
//             }}
//           >
//             Work Permit Details
//           </Typography>

//           <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap">
//             {permit?.work_type && (
//               <Chip
//                 label={permit?.work_type}
//                 color="primary"
//                 variant="outlined"
//                 size="small"
//               />
//             )}
//             {permit?.contractor && (
//               <Chip
//                 label={`Contractor: ${permit?.contractor}`}
//                 color="secondary"
//                 size="small"
//               />
//             )}
//             {permit?.department && (
//               <Chip
//                 label={`Dept: ${permit?.department}`}
//                 color="default"
//                 size="small"
//               />
//             )}
//           </Stack>

//           <Box
//             sx={{
//               display: "grid",
//               gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
//               gap: 2,
//             }}
//           >
//             <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
//               <Typography
//                 variant="subtitle2"
//                 sx={{
//                   fontWeight: 600,
//                   mb: 1.5,
//                   color: "text.secondary",
//                   fontSize: "0.85rem",
//                 }}
//               >
//                 General Information
//               </Typography>
//               <Stack spacing={1}>
//                 <DetailItem
//                   label="Permit Type"
//                   value={form1Data?.workPermitName}
//                 />
//                 <DetailItem label="Issued To" value={permit?.issued_to} />
//                 <DetailItem
//                   label="Receiver Name"
//                   value={permit?.receiver_name}
//                 />
//                 <DetailItem label="Area" value={permit?.area} />
//                 <DetailItem label="Section" value={permit?.section} />
//                 <DetailItem
//                   label="Work Location"
//                   value={permit?.work_location}
//                 />

//                 {(permit?.excavation_length ||
//                   permit?.excavation_width ||
//                   permit?.excavation_depth) && (
//                   <Box
//                     sx={{ mt: 2, p: 1.5, bgcolor: "grey.50", borderRadius: 1 }}
//                   >
//                     <Typography
//                       variant="subtitle2"
//                       sx={{ fontWeight: 600, mb: 1, fontSize: "0.85rem" }}
//                     >
//                       Excavation Details
//                     </Typography>
//                     <Stack direction="row" spacing={2} flexWrap="wrap">
//                       {permit?.excavation_length && (
//                         <DetailItem
//                           label="Length"
//                           value={permit.excavation_length}
//                         />
//                       )}
//                       {permit?.excavation_width && (
//                         <DetailItem
//                           label="Width"
//                           value={permit.excavation_width}
//                         />
//                       )}
//                       {permit?.excavation_depth && (
//                         <DetailItem
//                           label="Depth"
//                           value={permit.excavation_depth}
//                         />
//                       )}
//                     </Stack>
//                   </Box>
//                 )}
//               </Stack>
//             </Paper>

//             <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
//               <Typography
//                 variant="subtitle2"
//                 sx={{
//                   fontWeight: 600,
//                   mb: 1.5,
//                   color: "text.secondary",
//                   fontSize: "0.85rem",
//                 }}
//               >
//                 Work Details
//               </Typography>
//               <Stack spacing={1}>
//                 <DetailItem
//                   label="Work Description"
//                   value={permit?.work_description}
//                 />
//                 <DetailItem label="No. of Men" value={permit?.no_of_men} />
//                 <DetailItem
//                   label="Start Date & Time"
//                   value={formatDateTime(dateValues["Start Date & Time"])}
//                 />
//                 <DetailItem
//                   label="End Date & Time"
//                   value={formatDateTime(dateValues["End Date & Time"])}
//                 />
//                 <DetailItem
//                   label="Work Time From"
//                   value={formatDateTime(dateValues["Work Time From"])}
//                 />
//                 <DetailItem
//                   label="Work Time To"
//                   value={formatDateTime(dateValues["Work Time To"])}
//                 />
//               </Stack>
//             </Paper>
//           </Box>
//         </CardContent>
//       </Card>
//     );
//   };

//   // Render department form
//   const renderDepartmentForm = () => {
//     if (!reviewDepartment) {
//       return (
//         <Paper sx={{ p: 3, textAlign: "center" }}>
//           <Typography color="textSecondary">
//             No department review required
//           </Typography>
//         </Paper>
//       );
//     }

//     const deptNameLower = reviewDepartment.department_name?.toLowerCase() || "";

//     return (
//       <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
//         <CardContent sx={{ p: 3 }}>
//           <Typography
//             variant="h6"
//             sx={{ mb: 2, fontWeight: 600, color: "primary.main" }}
//           >
//             {reviewDepartment.department_name}
//           </Typography>

//           {/* Mechanical Department */}
//           {deptNameLower.includes("mechanical") && (
//             <>
//               <Box sx={{ mb: 2 }}>
//                 <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
//                   Description of underground facilities existing at the proposed location
//                 </Typography>
//                 <textarea
//                   value={departmentFormData.underground_facilities_description || ""}
//                   onChange={(e) =>
//                     handleFieldChange("underground_facilities_description", e.target.value)
//                   }
//                   rows={3}
//                   style={{
//                     width: "100%",
//                     padding: "8px 10px",
//                     borderRadius: "4px",
//                     border: "1px solid #ccc",
//                     fontSize: "14px",
//                     fontFamily: "inherit",
//                   }}
//                 />
//               </Box>

//               <Box sx={{ mb: 2 }}>
//                 <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
//                   Precautions to be observed for safe excavation job
//                 </Typography>
//                 <textarea
//                   value={departmentFormData.precautions_safe_excavation || ""}
//                   onChange={(e) =>
//                     handleFieldChange("precautions_safe_excavation", e.target.value)
//                   }
//                   rows={3}
//                   style={{
//                     width: "100%",
//                     padding: "8px 10px",
//                     borderRadius: "4px",
//                     border: "1px solid #ccc",
//                     fontSize: "14px",
//                     fontFamily: "inherit",
//                   }}
//                 />
//               </Box>

//               <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
//                 <Box sx={{ mb: 2, flex: 1, minWidth: "200px" }}>
//                   <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
//                     Date
//                   </Typography>
//                   <input
//                     type="date"
//                     value={departmentFormData.date || ""}
//                     onChange={(e) => handleFieldChange("date", e.target.value)}
//                     style={{
//                       width: "100%",
//                       padding: "8px 10px",
//                       borderRadius: "4px",
//                       border: "1px solid #ccc",
//                       fontSize: "14px",
//                     }}
//                   />
//                 </Box>

//                 <Box sx={{ mb: 2, flex: 1, minWidth: "200px" }}>
//                   <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
//                     Name & Designation
//                   </Typography>
//                   <input
//                     type="text"
//                     value={departmentFormData.name_designation || ""}
//                     onChange={(e) =>
//                       handleFieldChange("name_designation", e.target.value)
//                     }
//                     style={{
//                       width: "100%",
//                       padding: "8px 10px",
//                       borderRadius: "4px",
//                       border: "1px solid #ccc",
//                       fontSize: "14px",
//                     }}
//                   />
//                 </Box>

//                 <Box sx={{ mb: 2, flex: 1, minWidth: "200px" }}>
//                   <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
//                     Signature
//                   </Typography>
//                   <input
//                     type="text"
//                     value={departmentFormData.signature || ""}
//                     onChange={(e) => handleFieldChange("signature", e.target.value)}
//                     style={{
//                       width: "100%",
//                       padding: "8px 10px",
//                       borderRadius: "4px",
//                       border: "1px solid #ccc",
//                       fontSize: "14px",
//                     }}
//                   />
//                 </Box>
//               </Box>
//             </>
//           )}

//           {/* Fire/Safety Department */}
//           {(deptNameLower.includes("fire") || deptNameLower.includes("safety")) && (
//             <>
//               <Box sx={{ mb: 2 }}>
//                 <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
//                   Provide:
//                 </Typography>
//                 <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
//                   <label style={{ display: "inline-flex", alignItems: "center", marginRight: "16px", cursor: "pointer" }}>
//                     <input
//                       type="checkbox"
//                       checked={departmentFormData.provide_cross_over || false}
//                       onChange={(e) => handleFieldChange("provide_cross_over", e.target.checked)}
//                       style={{ marginRight: "6px" }}
//                     />
//                     <Typography variant="body2">Cross over</Typography>
//                   </label>
//                   <label style={{ display: "inline-flex", alignItems: "center", marginRight: "16px", cursor: "pointer" }}>
//                     <input
//                       type="checkbox"
//                       checked={departmentFormData.provide_barricades || false}
//                       onChange={(e) => handleFieldChange("provide_barricades", e.target.checked)}
//                       style={{ marginRight: "6px" }}
//                     />
//                     <Typography variant="body2">Barricades</Typography>
//                   </label>
//                   <label style={{ display: "inline-flex", alignItems: "center", marginRight: "16px", cursor: "pointer" }}>
//                     <input
//                       type="checkbox"
//                       checked={departmentFormData.provide_road_blocks || false}
//                       onChange={(e) => handleFieldChange("provide_road_blocks", e.target.checked)}
//                       style={{ marginRight: "6px" }}
//                     />
//                     <Typography variant="body2">Road blocks</Typography>
//                   </label>
//                   <label style={{ display: "inline-flex", alignItems: "center", marginRight: "16px", cursor: "pointer" }}>
//                     <input
//                       type="checkbox"
//                       checked={departmentFormData.provide_shoring || false}
//                       onChange={(e) => handleFieldChange("provide_shoring", e.target.checked)}
//                       style={{ marginRight: "6px" }}
//                     />
//                     <Typography variant="body2">Shoring</Typography>
//                   </label>
//                   <label style={{ display: "inline-flex", alignItems: "center", marginRight: "16px", cursor: "pointer" }}>
//                     <input
//                       type="checkbox"
//                       checked={departmentFormData.provide_others || false}
//                       onChange={(e) => handleFieldChange("provide_others", e.target.checked)}
//                       style={{ marginRight: "6px" }}
//                     />
//                     <Typography variant="body2">Others</Typography>
//                   </label>
//                 </Box>
//               </Box>

//               <Box sx={{ mb: 2 }}>
//                 <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
//                   Types of excavation
//                 </Typography>
//                 <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
//                   <label style={{ display: "inline-flex", alignItems: "center", marginRight: "16px", cursor: "pointer" }}>
//                     <input
//                       type="radio"
//                       name="excavation_type"
//                       checked={departmentFormData.excavation_type === "Manual"}
//                       onChange={() => handleFieldChange("excavation_type", "Manual")}
//                       style={{ marginRight: "6px" }}
//                     />
//                     <Typography variant="body2">Manual</Typography>
//                   </label>
//                   <label style={{ display: "inline-flex", alignItems: "center", marginRight: "16px", cursor: "pointer" }}>
//                     <input
//                       type="radio"
//                       name="excavation_type"
//                       checked={departmentFormData.excavation_type === "Mechanical"}
//                       onChange={() => handleFieldChange("excavation_type", "Mechanical")}
//                       style={{ marginRight: "6px" }}
//                     />
//                     <Typography variant="body2">Mechanical</Typography>
//                   </label>
//                   <label style={{ display: "inline-flex", alignItems: "center", marginRight: "16px", cursor: "pointer" }}>
//                     <input
//                       type="radio"
//                       name="excavation_type"
//                       checked={departmentFormData.excavation_type === "Hand tools"}
//                       onChange={() => handleFieldChange("excavation_type", "Hand tools")}
//                       style={{ marginRight: "6px" }}
//                     />
//                     <Typography variant="body2">Hand tools</Typography>
//                   </label>
//                   <label style={{ display: "inline-flex", alignItems: "center", marginRight: "16px", cursor: "pointer" }}>
//                     <input
//                       type="radio"
//                       name="excavation_type"
//                       checked={departmentFormData.excavation_type === "Others"}
//                       onChange={() => handleFieldChange("excavation_type", "Others")}
//                       style={{ marginRight: "6px" }}
//                     />
//                     <Typography variant="body2">Others</Typography>
//                   </label>
//                 </Box>
//               </Box>

//               <Box sx={{ mb: 2 }}>
//                 <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
//                   Use following safety equipment:
//                 </Typography>
//                 <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
//                   <label style={{ display: "inline-flex", alignItems: "center", marginRight: "16px", cursor: "pointer" }}>
//                     <input
//                       type="checkbox"
//                       checked={departmentFormData.safety_equipment_gas_masks || false}
//                       onChange={(e) => handleFieldChange("safety_equipment_gas_masks", e.target.checked)}
//                       style={{ marginRight: "6px" }}
//                     />
//                     <Typography variant="body2">Gas Masks</Typography>
//                   </label>
//                   <label style={{ display: "inline-flex", alignItems: "center", marginRight: "16px", cursor: "pointer" }}>
//                     <input
//                       type="checkbox"
//                       checked={departmentFormData.safety_equipment_eye_protection || false}
//                       onChange={(e) => handleFieldChange("safety_equipment_eye_protection", e.target.checked)}
//                       style={{ marginRight: "6px" }}
//                     />
//                     <Typography variant="body2">Eye Protection</Typography>
//                   </label>
//                   <label style={{ display: "inline-flex", alignItems: "center", marginRight: "16px", cursor: "pointer" }}>
//                     <input
//                       type="checkbox"
//                       checked={departmentFormData.safety_equipment_ear_protection || false}
//                       onChange={(e) => handleFieldChange("safety_equipment_ear_protection", e.target.checked)}
//                       style={{ marginRight: "6px" }}
//                     />
//                     <Typography variant="body2">Ear Protection</Typography>
//                   </label>
//                   <label style={{ display: "inline-flex", alignItems: "center", marginRight: "16px", cursor: "pointer" }}>
//                     <input
//                       type="checkbox"
//                       checked={departmentFormData.safety_equipment_wooden_handheld || false}
//                       onChange={(e) => handleFieldChange("safety_equipment_wooden_handheld", e.target.checked)}
//                       style={{ marginRight: "6px" }}
//                     />
//                     <Typography variant="body2">Wooden Handheld tools</Typography>
//                   </label>
//                   <label style={{ display: "inline-flex", alignItems: "center", marginRight: "16px", cursor: "pointer" }}>
//                     <input
//                       type="checkbox"
//                       checked={departmentFormData.safety_equipment_others || false}
//                       onChange={(e) => handleFieldChange("safety_equipment_others", e.target.checked)}
//                       style={{ marginRight: "6px" }}
//                     />
//                     <Typography variant="body2">Others</Typography>
//                   </label>
//                 </Box>
//               </Box>

//               <Box sx={{ mb: 2 }}>
//                 <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
//                   Provide following:
//                 </Typography>
//                 <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
//                   <label style={{ display: "inline-flex", alignItems: "center", marginRight: "16px", cursor: "pointer" }}>
//                     <input
//                       type="checkbox"
//                       checked={departmentFormData.emergency_approach_for_vehicles || false}
//                       onChange={(e) => handleFieldChange("emergency_approach_for_vehicles", e.target.checked)}
//                       style={{ marginRight: "6px" }}
//                     />
//                     <Typography variant="body2">Emergency approach for vehicles</Typography>
//                   </label>
//                   <label style={{ display: "inline-flex", alignItems: "center", marginRight: "16px", cursor: "pointer" }}>
//                     <input
//                       type="checkbox"
//                       checked={departmentFormData.emergency_approach_barricading || false}
//                       onChange={(e) => handleFieldChange("emergency_approach_barricading", e.target.checked)}
//                       style={{ marginRight: "6px" }}
//                     />
//                     <Typography variant="body2">Barricading</Typography>
//                   </label>
//                   <label style={{ display: "inline-flex", alignItems: "center", marginRight: "16px", cursor: "pointer" }}>
//                     <input
//                       type="checkbox"
//                       checked={departmentFormData.emergency_approach_road_closure || false}
//                       onChange={(e) => handleFieldChange("emergency_approach_road_closure", e.target.checked)}
//                       style={{ marginRight: "6px" }}
//                     />
//                     <Typography variant="body2">Road closure</Typography>
//                   </label>
//                 </Box>
//               </Box>

//               <Box sx={{ mb: 2 }}>
//                 <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
//                   Other precautions
//                 </Typography>
//                 <textarea
//                   value={departmentFormData.other_precautions || ""}
//                   onChange={(e) => handleFieldChange("other_precautions", e.target.value)}
//                   rows={3}
//                   style={{
//                     width: "100%",
//                     padding: "8px 10px",
//                     borderRadius: "4px",
//                     border: "1px solid #ccc",
//                     fontSize: "14px",
//                     fontFamily: "inherit",
//                   }}
//                 />
//               </Box>
//             </>
//           )}

//           {!deptNameLower.includes("mechanical") &&
//             !deptNameLower.includes("electrician") &&
//             !deptNameLower.includes("electrical") &&
//             !deptNameLower.includes("electician") &&
//             !deptNameLower.includes("instrument") &&
//             !deptNameLower.includes("fire") &&
//             !deptNameLower.includes("safety") && (
//               <Typography color="textSecondary">
//                 No specific fields configured for this department:{" "}
//                 {reviewDepartment.department_name}
//               </Typography>
//             )}

//           <Box sx={{ mt: 3 }}>
//             <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
//               General Observations
//             </Typography>
//             <textarea
//               value={departmentFormData.observations || ""}
//               onChange={(e) => handleFieldChange("observations", e.target.value)}
//               rows={4}
//               placeholder="Enter your observations here..."
//               style={{
//                 width: "100%",
//                 padding: "8px 10px",
//                 borderRadius: "4px",
//                 border: "1px solid #ccc",
//                 fontSize: "14px",
//                 fontFamily: "inherit",
//               }}
//             />
//           </Box>

//           <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
//             <Button
//               variant="contained"
//               onClick={handleSaveDepartment}
//               disabled={isLoading || !reviewDepartment}
//               sx={{ backgroundColor: "green" }}
//             >
//               {isLoading ? (
//                 <CircularProgress size={20} color="inherit" />
//               ) : (
//                 "Save"
//               )}
//             </Button>
//           </Box>
//         </CardContent>
//       </Card>
//     );
//   };

//   return (
//     <div
//       className={`data-list-content-container page-content ${
//         isSidebarExpanded ? "expanded" : "collapsed"
//       }`}
//     >
//       <div className="data-form-content">
//         <div className="data-form-header">
//           <div className="data-form-header-content">
//             <div className="data-form-heading-content-left-side">
//               <IoArrowBackCircle
//                 style={{
//                   color: "#233b80",
//                   paddingTop: "3px",
//                   cursor: "pointer",
//                 }}
//                 size={25}
//                 onClick={handleCancelClick}
//               />
//               <span className="data-form-heading-text">
//                 Excavation Clearance / Road Closure Work Permit View
//                 {reviewDepartment && ` - ${reviewDepartment.department_name}`}
//               </span>
//             </div>
//           </div>
//         </div>
//         <Divider />

//         <div className="data-form-body-permit-fands">
//           <Box
//             sx={{
//               width: "100%",
//               mt: 2,
//               display: "flex",
//               flexDirection: "column",
//               gap: 2,
//             }}
//           >
//             {renderForm1()}
//             {renderDepartmentForm()}
//           </Box>
//         </div>

//         <div className="footer">
//           <div className="button-group" style={{ display: "flex", gap: 8 }}>
//             <Button variant="outlined" onClick={handleCancelClick}>
//               Cancel
//             </Button>
//             {canReject && (
//               <Button
//                 variant="contained"
//                 onClick={() => setShowRejectPopup(true)}
//                 sx={{ backgroundColor: "#d32f2f" }}
//               >
//                 Reject
//               </Button>
//             )}
//             {canApprove && (
//               <Button
//                 variant="contained"
//                 onClick={() => setShowApprovePopup(true)}
//                 sx={{ backgroundColor: "#2e7d32" }}
//               >
//                 Approve
//               </Button>
//             )}
//           </div>
//         </div>

//         {/* Approve Dialog */}
//         <Dialog
//           open={showApprovePopup}
//           onClose={() => !isLoading && setShowApprovePopup(false)}
//           maxWidth="sm"
//           fullWidth
//         >
//           <DialogTitle sx={{ fontWeight: 600, color: "primary.main" }}>
//             Excavation Approval
//           </DialogTitle>
//           <DialogContent>
//             <Typography sx={{ mb: 2 }}>
//               Are you sure you want to approve this permit?
//             </Typography>
//             <TextField
//               label="Remark"
//               multiline
//               rows={4}
//               fullWidth
//               value={remark}
//               onChange={(e) => setRemark(e.target.value)}
//               placeholder="Enter remark for approval"
//               sx={{ mb: 2 }}
//             />
//             <Box>
//               <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
//                 Attach File
//               </Typography>
//               <input
//                 type="file"
//                 onChange={(e) => setAttachmentFile(e.target.files[0])}
//                 style={{
//                   padding: "8px",
//                   border: "1px solid #ccc",
//                   borderRadius: "4px",
//                   width: "100%",
//                 }}
//               />
//             </Box>
//           </DialogContent>
//           <DialogActions sx={{ p: 2 }}>
//             <Button
//               onClick={() => setShowApprovePopup(false)}
//               disabled={isLoading}
//             >
//               Cancel
//             </Button>
//             <Button
//               variant="contained"
//               onClick={handleApprove}
//               disabled={isLoading}
//               sx={{ backgroundColor: "#2e7d32" }}
//             >
//               {isLoading ? <CircularProgress size={20} /> : "Approve"}
//             </Button>
//           </DialogActions>
//         </Dialog>

//         {/* Reject Dialog */}
//         <Dialog
//           open={showRejectPopup}
//           onClose={() => !isLoading && setShowRejectPopup(false)}
//           maxWidth="sm"
//           fullWidth
//         >
//           <DialogTitle sx={{ fontWeight: 600, color: "error.main" }}>
//             Excavation Rejection
//           </DialogTitle>
//           <DialogContent>
//             <Typography sx={{ mb: 2 }}>
//               Are you sure you want to reject this permit?
//             </Typography>
//             <TextField
//               label="Remark"
//               multiline
//               rows={4}
//               fullWidth
//               value={remark}
//               onChange={(e) => setRemark(e.target.value)}
//               placeholder="Enter remark for rejection"
//               sx={{ mb: 2 }}
//             />
//             <Box>
//               <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
//                 Attach File
//               </Typography>
//               <input
//                 type="file"
//                 onChange={(e) => setAttachmentFile(e.target.files[0])}
//                 style={{
//                   padding: "8px",
//                   border: "1px solid #ccc",
//                   borderRadius: "4px",
//                   width: "100%",
//                 }}
//               />
//             </Box>
//           </DialogContent>
//           <DialogActions sx={{ p: 2 }}>
//             <Button
//               onClick={() => setShowRejectPopup(false)}
//               disabled={isLoading}
//             >
//               Cancel
//             </Button>
//             <Button
//               variant="contained"
//               onClick={handleReject}
//               disabled={isLoading}
//               sx={{ backgroundColor: "#d32f2f" }}
//             >
//               {isLoading ? <CircularProgress size={20} /> : "Reject"}
//             </Button>
//           </DialogActions>
//         </Dialog>

//         <Snackbar
//           open={snackbarOpen}
//           autoHideDuration={6000}
//           onClose={handleSnackbarClose}
//           anchorOrigin={{ vertical: "top", horizontal: "right" }}
//         >
//           <MuiAlert
//             onClose={handleSnackbarClose}
//             severity={severity}
//             variant="filled"
//           >
//             {snackbarMessage}
//           </MuiAlert>
//         </Snackbar>
//       </div>
//     </div>
//   );
// }

// export default ExcavationPermitViewPage;

// type="text"
//                     value={departmentFormData.signature || ""}
//                     onChange={(e) => handleFieldChange("signature", e.target.value)}
//                     style={{
//                       width: "100%",
//                       padding: "8px 10px",
//                       borderRadius: "4px",
//                       border: "1px solid #ccc",
//                       fontSize: "14px",
//                     }}
//                   />
//                 </Box>
//               </Box>
//             </>
//           )}

//           {/* Electrical Department */}
//           {(deptNameLower.includes("electrician") ||
//             deptNameLower.includes("electrical") ||
//             deptNameLower.includes("electician")) && (
//             <>
//               <Box sx={{ mb: 2 }}>
//                 <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
//                   Details of underground cables present in the area to be excavated
//                 </Typography>
//                 <textarea
//                   value={departmentFormData.underground_cables_details || ""}
//                   onChange={(e) =>
//                     handleFieldChange("underground_cables_details", e.target.value)
//                   }
//                   rows={3}
//                   style={{
//                     width: "100%",
//                     padding: "8px 10px",
//                     borderRadius: "4px",
//                     border: "1px solid #ccc",
//                     fontSize: "14px",
//                     fontFamily: "inherit",
//                   }}
//                 />
//               </Box>

//               <Box sx={{ mb: 2 }}>
//                 <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
//                   Electrical isolation required
//                 </Typography>
//                 <textarea
//                   value={departmentFormData.electrical_isolation_required || ""}
//                   onChange={(e) =>
//                     handleFieldChange("electrical_isolation_required", e.target.value)
//                   }
//                   rows={3}
//                   style={{
//                     width: "100%",
//                     padding: "8px 10px",
//                     borderRadius: "4px",
//                     border: "1px solid #ccc",
//                     fontSize: "14px",
//                     fontFamily: "inherit",
//                   }}
//                 />
//               </Box>

//               <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
//                 <Box sx={{ mb: 2, flex: 1, minWidth: "200px" }}>
//                   <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
//                     Date
//                   </Typography>
//                   <input
//                     type="date"
//                     value={departmentFormData.date || ""}
//                     onChange={(e) => handleFieldChange("date", e.target.value)}
//                     style={{
//                       width: "100%",
//                       padding: "8px 10px",
//                       borderRadius: "4px",
//                       border: "1px solid #ccc",
//                       fontSize: "14px",
//                     }}
//                   />
//                 </Box>

//                 <Box sx={{ mb: 2, flex: 1, minWidth: "200px" }}>
//                   <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
//                     Name & Designation
//                   </Typography>
//                   <input
//                     type="text"
//                     value={departmentFormData.name_designation || ""}
//                     onChange={(e) =>
//                       handleFieldChange("name_designation", e.target.value)
//                     }
//                     style={{
//                       width: "100%",
//                       padding: "8px 10px",
//                       borderRadius: "4px",
//                       border: "1px solid #ccc",
//                       fontSize: "14px",
//                     }}
//                   />
//                 </Box>

//                 <Box sx={{ mb: 2, flex: 1, minWidth: "200px" }}>
//                   <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
//                     Signature
//                   </Typography>
//                   <input
//                     type="text"
//                     value={departmentFormData.signature || ""}
//                     onChange={(e) => handleFieldChange("signature", e.target.value)}
//                     style={{
//                       width: "100%",
//                       padding: "8px 10px",
//                       borderRadius: "4px",
//                       border: "1px solid #ccc",
//                       fontSize: "14px",
//                     }}
//                   />
//                 </Box>
//               </Box>
//             </>
//           )}

//           {/* Instrument Department */}
//           {deptNameLower.includes("instrument") && (
//             <>
//               <Box sx={{ mb: 2 }}>
//                 <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
//                   Details of underground cables present in the area to be excavated
//                 </Typography>
//                 <textarea
//                   value={departmentFormData.underground_cables_details || ""}
//                   onChange={(e) =>
//                     handleFieldChange("underground_cables_details", e.target.value)
//                   }
//                   rows={3}
//                   style={{
//                     width: "100%",
//                     padding: "8px 10px",
//                     borderRadius: "4px",
//                     border: "1px solid #ccc",
//                     fontSize: "14px",
//                     fontFamily: "inherit",
//                   }}
//                 />
//               </Box>

//               <Box sx={{ mb: 2 }}>
//                 <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
//                   Precautions to be observed for safe excavation job
//                 </Typography>
//                 <textarea
//                   value={departmentFormData.precautions_safe_excavation || ""}
//                   onChange={(e) =>
//                     handleFieldChange("precautions_safe_excavation", e.target.value)
//                   }
//                   rows={3}
//                   style={{
//                     width: "100%",
//                     padding: "8px 10px",
//                     borderRadius: "4px",
//                     border: "1px solid #ccc",
//                     fontSize: "14px",
//                     fontFamily: "inherit",
//                   }}
//                 />
//               </Box>

//               <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
//                 <Box sx={{ mb: 2, flex: 1, minWidth: "200px" }}>
//                   <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
//                     Date
//                   </Typography>
//                   <input
//                     type="date"
//                     value={departmentFormData.date || ""}
//                     onChange={(e) => handleFieldChange("date", e.target.value)}
//                     style={{
//                       width: "100%",
//                       padding: "8px 10px",
//                       borderRadius: "4px",
//                       border: "1px solid #ccc",
//                       fontSize: "14px",
//                     }}
//                   />
//                 </Box>

//                 <Box sx={{ mb: 2, flex: 1, minWidth: "200px" }}>
//                   <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
//                     Name & Designation
//                   </Typography>
//                   <input
//                     type="text"
//                     value={departmentFormData.name_designation || ""}
//                     onChange={(e) =>
//                       handleFieldChange("name_designation", e.target.value)
//                     }
//                     style={{
//                       width: "100%",
//                       padding: "8px 10px",
//                       borderRadius: "4px",
//                       border: "1px solid #ccc",
//                       fontSize: "14px",
//                     }}
//                   />
//                 </Box>

//                 <Box sx={{ mb: 2, flex: 1, minWidth: "200px" }}>
//                   <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
//                     Signature
//                   </Typography>
//                   <input
